import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LuEllipsisVertical, LuPlus, LuSearch, LuX } from "react-icons/lu";
import VendorLayout from "./VendorLayout";
import TrachRedIcon from "../icons/TrachRedIcon";
import EditGrayIcon from "../icons/EditGrayIcon";
import InfoGrayIcon from "../icons/InfoGrayIcon";
import productController from "../controllers/productController";
import vendorMenuController from "../controllers/vendorMenuController";
import { userService } from "../services/userService";
import ArrowUpExpIcon from "../icons/Arrows/ArrowUpExpIcon";
import DownArrowImpIcon from "../icons/Arrows/DownArrowImpIcon";
import ProductImportModal from "./ProductImportModal";
import ProductExportModal from "./ProductExportModal";
import exportVendorProducts from "./exportVendorProducts";
import { translateRestaurantCategory } from "../translations/restaurantCategories";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";

// Translate application-owned feedback only; preserve provider details verbatim.
const menuFeedbackLabels = new Map([
  ["Could not load add-on sections.", "txt_web_vendor_could_not_load_add_on_sections"],
  ["Add-on section name is required.", "txt_web_vendor_add_on_section_name_is_required"],
  ["Could not save add-on section.", "txt_web_vendor_could_not_save_add_on_section"],
  ["Missing add-on section id.", "txt_web_vendor_missing_add_on_section_id"],
  ["Add-on item name is required.", "txt_web_vendor_add_on_item_name_is_required"],
  ["Add-on item price is required.", "txt_web_vendor_add_on_item_price_is_required"],
  ["Could not save add-on item.", "txt_web_vendor_could_not_save_add_on_item"],
  ["Could not delete add-on item.", "txt_web_vendor_could_not_delete_add_on_item"],
  ["Could not delete add-on section.", "txt_web_vendor_could_not_delete_add_on_section"],
  ["Category name is required.", "txt_web_vendor_category_name_is_required"],
  ["Could not delete category: missing category id.", "txt_web_vendor_could_not_delete_category_missing_category_id"],
  ["Could not update category. Check fields and try again.", "txt_web_vendor_could_not_update_category_check_fields_and_try_again"],
  ["Could not add category. Check fields and try again.", "txt_web_vendor_could_not_add_category_check_fields_and_try_again"],
  ["Could not load menu categories.", "txt_web_vendor_could_not_load_menu_categories"],
  ["Could not delete menu category.", "txt_web_vendor_could_not_delete_menu_category"],
  ["Could not load menu items.", "txt_web_vendor_could_not_load_menu_items"],
  ["Could not update menu item status.", "txt_web_vendor_could_not_update_menu_item_status"],
  ["Missing product id for product toggle.", "txt_web_vendor_missing_product_id_for_product_toggle"],
  ["Could not delete menu item.", "txt_web_vendor_could_not_delete_menu_item"],
  ["Missing item id.", "txt_web_vendor_missing_item_id"],
  ["Missing vendor session.", "txt_web_vendor_missing_vendor_session"],
  ["Missing user data.", "txt_web_vendor_missing_user_data"],
]);
const translateMenuFeedback = (message) => {
  const id = menuFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};


const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? response;

const isApiError = (response) => response?.isAxiosError || response?.response;

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent);

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data ?? error?.data;

  if (typeof data === "string") return data;

  return (
    data?.message ||
    data?.error ||
    data?.details ||
    data?.errors?.[0]?.message ||
    error?.message ||
    fallback
  );
};

const CATEGORY_LABEL_BY_VALUE = {
  CROCERIES: "Grocery",
  GROCERIES: "Grocery",
  GROCERY: "Grocery",
};

const PRODUCT_CATEGORY_TYPES = [
  "APPETIZERS",
  "MAIN_COURSES",
  "DESSERTS",
  "SOFT_DRINKS",
];

const MENU_ITEMS_PAGE_SIZE_OPTIONS = [10, 20, 50];

const toDisplayLabel = (value) => {
  if (!value) return "";

  const normalized = String(value)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");

  if (CATEGORY_LABEL_BY_VALUE[normalized]) {
    return CATEGORY_LABEL_BY_VALUE[normalized];
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const DEFAULT_MENU_CATEGORY_ROWS = PRODUCT_CATEGORY_TYPES.map(
  (categoryType, index) => ({
    active: true,
    category: categoryType,
    categoryType,
    custom: false,
    displayOrder: index,
    id: categoryType,
    items: [],
    name: toDisplayLabel(categoryType),
  }),
);

const toCategoryName = (item) =>
  toDisplayLabel(
    item.menuCategoryName ||
      item.assignedCategoryName ||
      item.categoryName ||
      item.subCategoryName ||
      item.categoryType ||
      item.category,
  ) || "Uncategorized";

const getCategoryRowName = (categoryItem = {}) =>
  firstDefined(
    categoryItem?.categoryName,
    categoryItem?.name,
    categoryItem?.category,
    categoryItem?.menuCategoryName,
    "",
  );

const getCategoryRowIds = (categoryItem = {}) =>
  [
    categoryItem.menuCategoryId,
    categoryItem.menuCategoryID,
    categoryItem.id,
    categoryItem.categoryId,
    categoryItem.categoryID,
  ]
    .filter(Boolean)
    .map((value) => String(value));

const getCategoryEditId = (categoryItem = {}) =>
  firstDefined(
    categoryItem.menuCategoryId,
    categoryItem.menuCategoryID,
    categoryItem.id,
    categoryItem.categoryId,
    categoryItem.categoryID,
    "",
  );

const categoryHasId = (categoryItem = {}, id) => {
  if (!id) return false;

  return getCategoryRowIds(categoryItem).includes(String(id));
};

const toPriceLabel = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "--";
  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) return String(value);

  return currency === "USD"
    ? `$${numericValue.toFixed(2)}`
    : `${numericValue.toFixed(2)} ${currency}`;
};

const isUsablePriceValue = (value) =>
  isPresent(value) && String(value).trim() !== "--";

const firstPriceValue = (...values) => values.find(isUsablePriceValue);

const getPriceValue = (item = {}, product = {}, basicInfo = {}, sku = {}) =>
  firstPriceValue(
    String(
      firstDefined(item.currency, product.currency, sku.currency, "USD"),
    ).toUpperCase() === "LBP"
      ? firstPriceValue(item.priceLBP, product.priceLBP)
      : firstPriceValue(item.priceUSD, product.priceUSD),
    item.price,
    item.productPrice,
    item.unitPrice,
    item.basePrice,
    item.salePrice,
    item.sellingPrice,
    item.costPriceValue,
    item.costPrice,
    product.price,
    product.productPrice,
    product.unitPrice,
    product.basePrice,
    product.salePrice,
    product.sellingPrice,
    product.costPriceValue,
    product.costPrice,
    basicInfo.price,
    basicInfo.productPrice,
    basicInfo.unitPrice,
    basicInfo.basePrice,
    basicInfo.salePrice,
    basicInfo.sellingPrice,
    basicInfo.costPriceValue,
    basicInfo.costPrice,
    sku.price,
    sku.productPrice,
    sku.unitPrice,
    sku.basePrice,
    sku.salePrice,
    sku.sellingPrice,
    sku.costPriceValue,
    sku.costPrice,
    "",
  );

export const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.productRequests)) return payload.productRequests;
  if (Array.isArray(payload?.categories)) return payload.categories;
  if (Array.isArray(payload?.menuItems)) return payload.menuItems;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === "object") {
    return getRows(payload.data);
  }
  if (payload?.result && typeof payload.result === "object") {
    return getRows(payload.result);
  }

  return [];
};

const getFiniteNumber = (...values) => {
  for (const value of values) {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) return numericValue;
  }

  return null;
};

export const getCatalogPagination = (
  payload,
  rowCount,
  currentPage,
  sizePage,
) => {
  const totalElements = getFiniteNumber(
    payload?.totalElements,
    payload?.totalElement,
    payload?.totalItems,
    payload?.totalCount,
    payload?.total,
    payload?.count,
  );
  const totalPages = getFiniteNumber(
    payload?.totalPages,
    payload?.pageCount,
    payload?.pages,
  );
  const hasKnownTotalPages = totalPages !== null && totalPages > 0;
  const inferredTotalPages = hasKnownTotalPages
    ? totalPages
    : Math.max(1, currentPage + (rowCount >= sizePage ? 1 : 0));
  const isLastPage =
    typeof payload?.last === "boolean"
      ? payload.last
      : hasKnownTotalPages
        ? currentPage >= totalPages
        : rowCount < sizePage;

  return {
    hasNextPage: !isLastPage,
    sizePage,
    totalElements,
    totalPages: inferredTotalPages,
  };
};

const getCategoryApiCardKey = (categoryItem = {}, index = 0) =>
  String(categoryItem.menuCategoryId || categoryItem.menuCategoryID || index);

const getCategoryApiActive = (categoryItem = {}) =>
  categoryItem.active === true;

const getSkuRows = (item = {}) => {
  const skuSource =
    item.skus ||
    item.skuList ||
    item.productSkus ||
    item.productSKUS ||
    item.variants ||
    item.portions ||
    item.portionList ||
    item.portionOptions ||
    item.menuPortions;

  if (Array.isArray(skuSource)) return skuSource;

  const singleSku =
    item.sku ||
    item.skuDto ||
    item.productSku ||
    item.productSKU ||
    item.defaultSku ||
    item.defaultSKU;

  return singleSku && typeof singleSku === "object" ? [singleSku] : [];
};

const normalizeCategoryValue = (value) =>
  String(Array.isArray(value) ? value[0] : value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getExactProductCategoryType = (value) => {
  const normalizedValue = normalizeCategoryValue(value);

  return PRODUCT_CATEGORY_TYPES.includes(normalizedValue)
    ? normalizedValue
    : "";
};

const isTrueValue = (value) =>
  value === true || String(value).trim().toLowerCase() === "true";

const textMentionsGrocery = (value) => {
  const normalizedValue = String(value || "").toLowerCase();

  return (
    normalizedValue.includes("grocery") ||
    normalizedValue.includes("grocer") ||
    normalizedValue.includes("crocer")
  );
};

const objectHasGroceryBusinessDescriptor = (source, seen = new WeakSet()) => {
  if (!source) return false;
  if (typeof source === "string") return textMentionsGrocery(source);
  if (typeof source !== "object") return false;
  if (seen.has(source)) return false;
  seen.add(source);

  if (Array.isArray(source)) {
    return source.some((item) =>
      objectHasGroceryBusinessDescriptor(item, seen),
    );
  }

  return Object.entries(source).some(([key, value]) => {
    const normalizedKey = String(key || "").toLowerCase();
    const isBusinessDescriptorKey =
      normalizedKey.includes("business") ||
      normalizedKey.includes("profile") ||
      normalizedKey.includes("category") ||
      normalizedKey.includes("type") ||
      normalizedKey.includes("portal");

    if (isBusinessDescriptorKey && textMentionsGrocery(value)) return true;

    return objectHasGroceryBusinessDescriptor(value, seen);
  });
};

const isGroceryVendorPortal = () =>
  textMentionsGrocery(window.location.pathname) ||
  objectHasGroceryBusinessDescriptor(userService.getUserPayload?.()) ||
  objectHasGroceryBusinessDescriptor(userService.getUserObject?.()) ||
  objectHasGroceryBusinessDescriptor(userService.getUserData?.());

const getDefaultCategoryRowsForVendor = () =>
  isGroceryVendorPortal() ? [] : DEFAULT_MENU_CATEGORY_ROWS;

const isProductImageRemoved = () => false;

const getCategoryRemovalKeys = (category = {}) =>
  [
    category.id,
    category.categoryId,
    category.categoryID,
    category.menuCategoryId,
    category.menuCategoryID,
    category.rawCategory?.id,
    category.rawCategory?.categoryId,
    category.rawCategory?.categoryID,
    category.rawCategory?.menuCategoryId,
    category.rawCategory?.menuCategoryID,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

const isCategoryRemoved = () => false;

const setCategoryRemoved = () => {};

const clearRemovedCategoryDisplayFromItems = (items = []) => items;

const syncSavedCategoryDisplayOnItems = (
  items = [],
  previousCategory = {},
  savedCategory = {},
) => {
  const previousKeys = new Set(getCategoryRemovalKeys(previousCategory));
  const savedKeys = getCategoryRemovalKeys(savedCategory);
  savedKeys.forEach((key) => previousKeys.add(key));

  if (previousKeys.size === 0) return items;

  const nextName = firstDefined(
    savedCategory.name,
    savedCategory.categoryName,
    savedCategory.menuCategoryName,
    "",
  );
  const nextMenuCategoryId = firstDefined(
    savedCategory.menuCategoryId,
    savedCategory.menuCategoryID,
    savedCategory.id,
    "",
  );
  const nextCategoryId = firstDefined(
    savedCategory.categoryId,
    savedCategory.categoryID,
    nextMenuCategoryId,
  );

  return items.map((item) => {
    const itemKeys = [
      item.categoryId,
      item.categoryID,
      item.menuCategoryId,
      item.menuCategoryID,
      item.rawItem?.categoryId,
      item.rawItem?.categoryID,
      item.rawItem?.menuCategoryId,
      item.rawItem?.menuCategoryID,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (!itemKeys.some((key) => previousKeys.has(key))) return item;

    return {
      ...item,
      category: nextName || item.category,
      categoryId: nextCategoryId || item.categoryId,
      categoryName: nextName || item.categoryName,
      menuCategoryId: nextMenuCategoryId || item.menuCategoryId,
      menuCategoryName: nextName || item.menuCategoryName,
    };
  });
};

const getItemStatusOverride = () => null;

const toOptionalBoolean = (value) => {
  if (!isPresent(value)) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const normalizedValue = normalizeCategoryValue(value);

  if (
    ["FALSE", "0", "NO", "N", "INACTIVE", "DISABLED", "UNAVAILABLE"].includes(
      normalizedValue,
    )
  ) {
    return false;
  }

  if (
    ["TRUE", "1", "YES", "Y", "ACTIVE", "ENABLED", "AVAILABLE"].includes(
      normalizedValue,
    )
  ) {
    return true;
  }

  return Boolean(value);
};

const getProductCategoryMatch = (value) => {
  const normalizedValue = normalizeCategoryValue(value);

  if (PRODUCT_CATEGORY_TYPES.includes(normalizedValue)) return normalizedValue;
  if (
    normalizedValue.includes("APPETIZER") ||
    normalizedValue.includes("STARTER")
  ) {
    return "APPETIZERS";
  }
  if (
    normalizedValue.includes("MAIN_COURSE") ||
    normalizedValue.includes("MAIN_DISH") ||
    normalizedValue.includes("MEAL")
  ) {
    return "MAIN_COURSES";
  }
  if (
    normalizedValue.includes("DESSERT") ||
    normalizedValue.includes("SWEET") ||
    normalizedValue.includes("CAKE")
  ) {
    return "DESSERTS";
  }
  if (
    normalizedValue.includes("DRINK") ||
    normalizedValue.includes("BEVERAGE") ||
    normalizedValue.includes("SODA") ||
    normalizedValue.includes("JUICE") ||
    normalizedValue.includes("SOFT")
  ) {
    return "SOFT_DRINKS";
  }

  return "";
};

const getProtectedCategoryType = (categoryItem = {}) => {
  if (
    isTrueValue(categoryItem.custom) ||
    isTrueValue(categoryItem.rawCategory?.custom)
  ) {
    return "";
  }

  const displayValues = [
    categoryItem.name,
    categoryItem.categoryName,
    categoryItem.menuCategoryName,
    categoryItem.category,
    categoryItem.rawCategory?.name,
    categoryItem.rawCategory?.categoryName,
    categoryItem.rawCategory?.menuCategoryName,
    categoryItem.rawCategory?.category,
  ];
  const displayMatch = displayValues
    .map(getExactProductCategoryType)
    .find(Boolean);

  if (displayMatch) return displayMatch;

  if (displayValues.some(isPresent)) return "";

  return (
    [
      categoryItem.categoryType,
      categoryItem.type,
      categoryItem.rawCategory?.categoryType,
      categoryItem.rawCategory?.type,
    ]
      .map(getExactProductCategoryType)
      .find(Boolean) || ""
  );
};

const isProtectedCategory = (categoryItem = {}) =>
  Boolean(getProtectedCategoryType(categoryItem));

const getDeletableCategoryId = (categoryItem = {}) =>
  firstDefined(
    categoryItem?.menuCategoryId,
    categoryItem?.menuCategoryID,
    categoryItem?.rawCategory?.menuCategoryId,
    categoryItem?.rawCategory?.menuCategoryID,
    categoryItem?.id,
    categoryItem?.categoryId,
    categoryItem?.categoryID,
    categoryItem?.rawCategory?.categoryId,
    categoryItem?.rawCategory?.categoryID,
    "",
  );

const toCategoryKeySet = (values) =>
  new Set(
    values
      .flatMap((value) => [
        normalizeCategoryValue(value),
        getProductCategoryMatch(value),
      ])
      .filter(Boolean),
  );

const toExactCategoryKeySet = (values) =>
  new Set(values.map(normalizeCategoryValue).filter(Boolean));

const getCategoryComparisonKeys = (categoryItem = {}) =>
  toExactCategoryKeySet([
    categoryItem.protectedType,
    getProtectedCategoryType(categoryItem),
    categoryItem.name,
    categoryItem.categoryName,
    categoryItem.menuCategoryName,
    categoryItem.category,
    categoryItem.rawCategory?.name,
    categoryItem.rawCategory?.categoryName,
    categoryItem.rawCategory?.menuCategoryName,
    categoryItem.rawCategory?.category,
  ]);

const getCategoryIdComparisonKeys = (categoryItem = {}) => {
  const protectedType = getProtectedCategoryType(categoryItem);
  const menuCategoryIds = [
    categoryItem.menuCategoryId,
    categoryItem.menuCategoryID,
    categoryItem.rawCategory?.menuCategoryId,
    categoryItem.rawCategory?.menuCategoryID,
    categoryItem.id,
    categoryItem.rawCategory?.id,
  ].filter(Boolean);
  const productCategoryIds = [
    categoryItem.categoryId,
    categoryItem.categoryID,
    categoryItem.rawCategory?.categoryId,
    categoryItem.rawCategory?.categoryID,
  ].filter(Boolean);
  const ids = protectedType
    ? [...menuCategoryIds, ...productCategoryIds]
    : [
        ...menuCategoryIds,
        ...(menuCategoryIds.length === 0 ? productCategoryIds : []),
      ];

  return ids.filter(Boolean).map((value) => String(value));
};

const getRawItemProductInfo = (rawItem = {}) => {
  const product =
    rawItem.product ||
    rawItem.productDetails ||
    rawItem.productDetail ||
    rawItem.productDto ||
    rawItem.details ||
    rawItem;
  const basicInfo =
    rawItem.basicInfo ||
    rawItem.basicInformation ||
    rawItem.basicInfoDto ||
    rawItem.productBasicInfo ||
    product.basicInfo ||
    product.basicInformation ||
    product.basicInfoDto ||
    product.productBasicInfo ||
    {};

  return { product, basicInfo };
};

const getItemCategoryKeys = (item = {}) => {
  const rawItem = item.rawItem || {};
  const { product, basicInfo } = getRawItemProductInfo(rawItem);

  return toCategoryKeySet([
    item.productCategory,
    item.category,
    item.categoryName,
    item.categoryType,
    item.menuCategoryName,
    rawItem.productCategory,
    rawItem.productCategoryType,
    rawItem.category,
    rawItem.categoryName,
    rawItem.categoryType,
    rawItem.menuCategoryName,
    product.productCategory,
    product.productCategoryType,
    product.category,
    product.categoryName,
    product.categoryType,
    product.menuCategoryName,
    basicInfo.category,
    basicInfo.productCategory,
    basicInfo.productCategoryType,
    basicInfo.categoryName,
    basicInfo.categoryType,
    basicInfo.menuCategoryName,
  ]);
};

const getItemCategoryIdKeys = (item = {}) =>
  [
    item.categoryId,
    item.categoryID,
    item.menuCategoryId,
    item.menuCategoryID,
    item.rawItem?.categoryId,
    item.rawItem?.categoryID,
    item.rawItem?.menuCategoryId,
    item.rawItem?.menuCategoryID,
    item.rawItem?.basicInfo?.categoryId,
    item.rawItem?.basicInfo?.categoryID,
  ]
    .filter(Boolean)
    .map((value) => String(value));

const getAssignedMenuCategoryKeys = (item = {}) => {
  const keys = toExactCategoryKeySet([
    item.menuCategoryName,
    item.assignedCategoryName,
    item.rawItem?.menuCategoryName,
    item.rawItem?.assignedCategoryName,
  ]);

  keys.delete("un category");
  keys.delete("uncategory");

  return keys;
};

const getAssignedMenuCategoryIdKeys = (item = {}) =>
  [
    item.menuCategoryId,
    item.menuCategoryID,
    item.rawItem?.menuCategoryId,
    item.rawItem?.menuCategoryID,
  ]
    .filter(Boolean)
    .map((value) => String(value));

const hasAssignedMenuCategory = (item = {}) =>
  getAssignedMenuCategoryIdKeys(item).length > 0 ||
  getAssignedMenuCategoryKeys(item).size > 0;

const categoryMatchesItem = (categoryItem = {}, item = {}) => {
  const categoryIds = getCategoryIdComparisonKeys(categoryItem);
  const assignedMenuCategoryIds = getAssignedMenuCategoryIdKeys(item);

  if (
    categoryIds.length > 0 &&
    assignedMenuCategoryIds.some((itemCategoryId) =>
      categoryIds.includes(itemCategoryId),
    )
  ) {
    return true;
  }

  const categoryKeys = getCategoryComparisonKeys(categoryItem);
  const assignedMenuCategoryKeys = getAssignedMenuCategoryKeys(item);

  if (hasAssignedMenuCategory(item)) {
    return Array.from(categoryKeys).some((key) =>
      assignedMenuCategoryKeys.has(key),
    );
  }

  const itemCategoryIds = getItemCategoryIdKeys(item);

  if (
    categoryIds.length > 0 &&
    itemCategoryIds.some((itemCategoryId) =>
      categoryIds.includes(itemCategoryId),
    )
  ) {
    return true;
  }

  const itemCategoryKeys = getItemCategoryKeys(item);

  return Array.from(categoryKeys).some((key) => itemCategoryKeys.has(key));
};

const toCountNumber = (value) => {
  if (!isPresent(value)) return null;
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const getExplicitCategoryCount = (categoryItem = {}) =>
  [
    categoryItem.itemCount,
    categoryItem.itemsCount,
    categoryItem.productCount,
    categoryItem.productsCount,
    categoryItem.totalItems,
    categoryItem.totalProducts,
    categoryItem.count,
    categoryItem.totalCount,
    categoryItem.rawCategory?.itemCount,
    categoryItem.rawCategory?.itemsCount,
    categoryItem.rawCategory?.productCount,
    categoryItem.rawCategory?.productsCount,
    categoryItem.rawCategory?.totalItems,
    categoryItem.rawCategory?.totalProducts,
    categoryItem.rawCategory?.count,
    categoryItem.rawCategory?.totalCount,
  ]
    .map(toCountNumber)
    .find((value) => value !== null);

const getCategoryItemCount = (categoryItem = {}, items = []) => {
  const matchedItemCount = items.filter((item) =>
    categoryMatchesItem(categoryItem, item),
  ).length;
  const nestedItemCount = Array.isArray(categoryItem.items)
    ? categoryItem.items.length
    : null;
  const explicitItemCount = getExplicitCategoryCount(categoryItem);
  const counts = [matchedItemCount, nestedItemCount, explicitItemCount].filter(
    (value) => Number.isFinite(value),
  );

  if (!isProtectedCategory(categoryItem)) {
    const directCounts = [matchedItemCount, nestedItemCount].filter((value) =>
      Number.isFinite(value),
    );

    return directCounts.length > 0 ? Math.max(...directCounts) : 0;
  }

  return counts.length > 0 ? Math.max(...counts) : 0;
};

const getProductCategoryValue = (item = {}, product = {}, basicInfo = {}) =>
  [
    basicInfo.category,
    basicInfo.productCategory,
    basicInfo.productCategoryType,
    basicInfo.categoryType,
    item.productCategory,
    product.productCategory,
    item.productCategoryType,
    product.productCategoryType,
    item.categoryType,
    product.categoryType,
    item.category,
    product.category,
    item.categoryName,
    product.categoryName,
  ]
    .map(getProductCategoryMatch)
    .find(Boolean) || "";

const normalizeImageSource = (value) => {
  const imageSource = String(value || "").trim();
  if (!imageSource) return "";

  if (/^(https?:|data:|blob:|\/)/i.test(imageSource)) {
    return imageSource;
  }

  if (/^[A-Za-z0-9+/=]+$/.test(imageSource) && imageSource.length > 100) {
    return `data:image/jpeg;base64,${imageSource}`;
  }

  return imageSource;
};

const getImageValue = (image) => {
  if (typeof image === "string") return normalizeImageSource(image);

  return normalizeImageSource(
    firstDefined(
      image?.url,
      image?.imageUrl,
      image?.path,
      image?.fileUrl,
      image?.attachmentUrl,
      image?.base64,
      image?.data,
    ),
  );
};

const getFirstImageValue = (...imageGroups) => {
  for (const group of imageGroups) {
    if (Array.isArray(group)) {
      const foundImage = group.map(getImageValue).find(Boolean);
      if (foundImage) return foundImage;
    } else {
      const image = getImageValue(group);
      if (image) return image;
    }
  }

  return "";
};

const getRawAddOnSections = (source = {}) => {
  const product =
    source.product ||
    source.productDetails ||
    source.productDetail ||
    source.productDto ||
    source.details ||
    source;
  const rawItem = source.rawItem || {};
  const candidates = [
    source.addonSections,
    source.addOnSections,
    source.productAddOnSections,
    source.addonSectionRequests,
    source.data?.addonSections,
    source.data?.addOnSections,
    source.data?.productAddOnSections,
    source.result?.addonSections,
    source.result?.addOnSections,
    source.result?.productAddOnSections,
    product.addonSections,
    product.addOnSections,
    product.productAddOnSections,
    rawItem.addonSections,
    rawItem.addOnSections,
    rawItem.productAddOnSections,
  ];

  return (
    candidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length > 0,
    ) || []
  );
};

const getAddOnSectionBackendId = (section = {}) =>
  firstDefined(
    section.productAddOnSectionId,
    section.productAddonSectionId,
    section.addonSectionId,
    section.addonSectionID,
    section.addOnSectionId,
    "",
  );

const getAddOnSectionRowId = (section = {}, index = 0) =>
  firstDefined(
    getAddOnSectionBackendId(section),
    section.id,
    `addon-section-${index}`,
  );

const getAddOnSectionItems = (section = {}) =>
  (Array.isArray(section.additions) && section.additions) ||
  (Array.isArray(section.addonItems) && section.addonItems) ||
  (Array.isArray(section.items) && section.items) ||
  [];

const isAddOnSectionActive = (section = {}) => {
  const value = firstDefined(section.active, section.enabled, true);

  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    return !["false", "0", "no", "inactive", "disabled"].includes(
      value.trim().toLowerCase(),
    );
  }

  return Boolean(value);
};

const getAddOnSectionPricingType = (section = {}) => {
  const rawType = String(
    firstDefined(section.type, section.pricingType, ""),
  ).toLowerCase();

  if (rawType === "paid" || section.paid === true) return "paid";

  return getAddOnSectionItems(section).some(
    (item) => Number(firstDefined(item.additionValue, item.price, 0)) > 0,
  )
    ? "paid"
    : "free";
};

const getCompanyAddOnSectionRows = (payload) => {
  const rawSections = getRawAddOnSections(payload);
  const rows = rawSections.length > 0 ? rawSections : getRows(payload);
  const seen = new Set();

  return rows.filter((section, index) => {
    const id = getAddOnSectionBackendId(section);
    const name = firstDefined(section.name, section.title, "");
    const key = id
      ? `id:${String(id).trim().toLowerCase()}`
      : `name:${String(name).trim().toLowerCase() || index}`;

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const mapAddOnSectionRow = (section = {}, index = 0) => {
  const items = getAddOnSectionItems(section);
  const allowMultiple =
    section.allowsMultipleSelections === true ||
    section.allowMultipleSelections === true ||
    section.allowMultiple === true ||
    section.allowMultiple === "true";

  return {
    id: getAddOnSectionRowId(section, index),
    addonSectionId: getAddOnSectionBackendId(section),
    name: firstDefined(
      section.name,
      section.title,
      `Add-on Section ${index + 1}`,
    ),
    pricingType: getAddOnSectionPricingType(section),
    active: isAddOnSectionActive(section),
    allowMultiple,
    maxSelections: firstDefined(
      section.maxSelections,
      section.maxSelectionNumber,
      allowMultiple ? "" : "1",
    ),
    items,
    rawSection: section,
  };
};

const sanitizePositiveIntegerInput = (value) =>
  String(value || "").replace(/[^\d]/g, "");

const normalizeAddOnSelectionLimit = (value, allowMultiple) => {
  if (!allowMultiple) return 1;

  const parsed = Number(sanitizePositiveIntegerInput(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const sanitizeDecimalInput = (value) => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = cleaned.split(".");
  const decimals = decimalParts.join("").slice(0, 2);

  return decimalParts.length > 0 ? `${whole}.${decimals}` : whole;
};

const normalizeAddOnItemPrice = (value) => {
  const numeric = Number(String(value || "0").replace(/[^0-9.]/g, ""));

  return Number.isFinite(numeric) ? numeric : 0;
};

const getAddOnItemBackendId = (item = {}) =>
  firstDefined(
    item.productAddOnItemId,
    item.productAddOnItemID,
    item.productAddonItemId,
    item.addonItemId,
    item.addonItemID,
    item.additionId,
    item.productAdditionId,
    item.id,
    "",
  );

const getAddOnItemName = (item = {}, index = 0, fallback = `Option ${index + 1}`) =>
  firstDefined(
    item.name,
    item.addonName,
    item.additionName,
    item.additionTitle,
    item.productAddOnName,
    item.title,
    fallback,
  );

const getAddOnItemDisplayName = (item, index) => getAddOnItemName(item, index,
  translateLabel("txt_web_vendor_option_number", "Option {number}", { number: index + 1 }));
const getAddOnSectionDisplayName = (section, index) => firstDefined(
  section.rawSection?.name, section.rawSection?.title,
  translateLabel("txt_web_vendor_add_on_section_number", "Add-on Section {number}", { number: index + 1 }));

const getAddOnItemPrice = (item = {}) =>
  normalizeAddOnItemPrice(firstDefined(item.additionValue, item.price, 0));

const getAddOnItemCurrency = (item = {}) =>
  String(firstDefined(item.currency, "USD")).toUpperCase();

const formatAddOnItemPrice = (item = {}) => {
  const currency = getAddOnItemCurrency(item);
  const amount = getAddOnItemPrice(item);

  return `${currency === "USD" ? "$" : `${currency} `}${amount.toFixed(2)}`;
};

const getMenuItemKeys = (item) =>
  [
    item.productId,
    item.productID,
    item.menuItemId,
    item.itemId,
    item.id,
    item.skuId,
    item.skuID,
    item.name &&
      `${item.name}-${item.category || item.categoryName || ""}-${
        item.priceValue || item.price || ""
      }`,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

const getRawCategoryItemKeys = (item = {}) => {
  const product =
    item.product ||
    item.productDetails ||
    item.productDetail ||
    item.productDto ||
    item.details ||
    item;
  const basicInfo =
    item.basicInfo ||
    item.basicInformation ||
    item.basicInfoDto ||
    item.productBasicInfo ||
    product.basicInfo ||
    product.basicInformation ||
    product.basicInfoDto ||
    product.productBasicInfo ||
    {};

  return [
    item.productId,
    item.productID,
    product.productId,
    product.productID,
    item.menuItemId,
    item.menuItemID,
    item.itemId,
    item.itemID,
    product.menuItemId,
    product.menuItemID,
    product.itemId,
    product.itemID,
    item.id,
    product.id,
    item.skuId,
    item.skuID,
    product.skuId,
    product.skuID,
    item.productName,
    product.productName,
    basicInfo.productName,
    item.name,
    product.name,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
};

const applyCategoryNamesToItems = (items, categoryRows) => {
  const categoryNameById = new Map();
  const categoryNameByItemKey = new Map();
  const categoryIdByItemKey = new Map();

  categoryRows.forEach((categoryItem) => {
    const categoryName = getCategoryRowName(categoryItem);
    if (!categoryName) return;
    const categoryId = getCategoryRowIds(categoryItem)[0] || "";

    getCategoryRowIds(categoryItem).forEach((categoryId) => {
      categoryNameById.set(String(categoryId), categoryName);
    });

    const categoryItems = Array.isArray(categoryItem.items)
      ? categoryItem.items
      : [];

    categoryItems.forEach((item) => {
      getRawCategoryItemKeys(item).forEach((key) => {
        categoryNameByItemKey.set(key, categoryName);
        if (categoryId) categoryIdByItemKey.set(key, categoryId);
      });
    });
  });

  return items.map((item) => {
    if (hasAssignedMenuCategory(item)) {
      return item;
    }

    const itemKeys = getMenuItemKeys(item);
    const categoryNameFromItem = itemKeys
      .map((key) => categoryNameByItemKey.get(key))
      .find(Boolean);
    const categoryIdFromItem = itemKeys
      .map((key) => categoryIdByItemKey.get(key))
      .find(Boolean);
    const categoryName =
      categoryNameFromItem ||
      categoryNameById.get(String(item.categoryId || "")) ||
      categoryNameById.get(String(item.menuCategoryId || ""));

    if (!categoryName) {
      return item;
    }

    return {
      ...item,
      category: categoryName,
      categoryName,
      menuCategoryName: categoryName,
      menuCategoryId: categoryIdFromItem || item.menuCategoryId || "",
    };
  });
};

const getCatalogSourceName = (item) => {
  const product = item.product || item.productDetails || item.productDetail || item.productDto || item.details || item;
  const basicInfo = item.basicInfo || item.basicInformation || item.basicInfoDto || item.productBasicInfo
    || product.basicInfo || product.basicInformation || product.basicInfoDto || product.productBasicInfo || {};
  return firstDefined(item.productName, product.productName, basicInfo.productName,
    item.name, product.name, basicInfo.name, item.title, product.title);
};

export const mapCatalogItem = (item, index, categoryContext = {}) => {
  const product =
    item.product ||
    item.productDetails ||
    item.productDetail ||
    item.productDto ||
    item.details ||
    item;
  const basicInfo =
    item.basicInfo ||
    item.basicInformation ||
    item.basicInfoDto ||
    item.productBasicInfo ||
    product.basicInfo ||
    product.basicInformation ||
    product.basicInfoDto ||
    product.productBasicInfo ||
    {};
  const sku = getSkuRows(item)[0] || getSkuRows(product)[0] || {};
  const productCategory = getProductCategoryValue(item, product, basicInfo);
  const priceValue = getPriceValue(item, product, basicInfo, sku);
  const currency = String(
    firstDefined(item.currency, product.currency, sku.currency, "USD"),
  ).toUpperCase();
  const menuCategoryName = firstDefined(
    categoryContext.menuCategoryName,
    categoryContext.categoryName,
    categoryContext.name,
    item.menuCategoryName,
    product.menuCategoryName,
    basicInfo.menuCategoryName,
    "",
  );
  const menuCategoryId = firstDefined(
    categoryContext.menuCategoryId,
    categoryContext.menuCategoryID,
    categoryContext.categoryId,
    categoryContext.categoryID,
    item.menuCategoryId,
    item.menuCategoryID,
    product.menuCategoryId,
    product.menuCategoryID,
    basicInfo.menuCategoryId,
    basicInfo.menuCategoryID,
    "",
  );
  const hasMenuCategoryContext = Boolean(menuCategoryName || menuCategoryId);
  const status = String(
    firstDefined(
      item.status,
      item.productStatus,
      item.availabilityStatus,
      product.status,
      product.productStatus,
      product.availabilityStatus,
      "",
    ),
  ).toUpperCase();
  const images = Array.isArray(item.images) ? item.images : [];
  const activeValue = firstDefined(
    item.isActive,
    item.active,
    item.enabled,
    product.isActive,
    product.active,
    product.enabled,
    basicInfo.isActive,
    basicInfo.active,
  );
  const normalizedActiveValue = toOptionalBoolean(activeValue);
  const statusOverride = getItemStatusOverride(item);
  const isActiveFromApi =
    normalizedActiveValue !== undefined
      ? normalizedActiveValue
      : status
        ? !["INACTIVE", "DISABLED", "DELETED", "REJECTED"].includes(status)
        : true;
  const isActive = statusOverride
    ? Boolean(statusOverride.active)
    : isActiveFromApi;
  const isOutOfStock = statusOverride
    ? Boolean(statusOverride.outOfStock)
    : (toOptionalBoolean(firstDefined(item.outOfStock, product.outOfStock)) ??
      !isActive);

  const categoryType = firstDefined(
    hasMenuCategoryContext ? productCategory : "",
    item.categoryType,
    product.categoryType,
    item.category,
    product.category,
    categoryContext.categoryType,
    categoryContext.category,
    "",
  );
  const categoryName = firstDefined(
    hasMenuCategoryContext ? menuCategoryName : "",
    item.menuCategoryName,
    product.menuCategoryName,
    basicInfo.menuCategoryName,
    item.categoryName,
    item.subCategoryName,
    product.categoryName,
    product.subCategoryName,
    basicInfo.categoryName,
    categoryContext.categoryName,
    categoryContext.name,
    categoryContext.menuCategoryName,
    productCategory,
    categoryType,
  );
  const hasMenuItemIdentity = Boolean(
    firstDefined(
      item.menuItemID,
      item.menuItemId,
      item.itemID,
      item.itemId,
      product.menuItemID,
      product.menuItemId,
      product.itemID,
      product.itemId,
    ),
  );
  const fallbackProductId =
    !hasMenuCategoryContext && !hasMenuItemIdentity
      ? firstDefined(item.id, product.id, "")
      : "";

  return {
    id:
      item.menuItemID ||
      item.menuItemId ||
      product.menuItemID ||
      product.menuItemId ||
      item.productID ||
      item.productId ||
      product.productID ||
      product.productId ||
      item.menuItemID ||
      item.menuItemId ||
      item.itemID ||
      item.itemId ||
      product.menuItemID ||
      product.menuItemId ||
      product.itemID ||
      product.itemId ||
      item.skuID ||
      item.skuId ||
      product.skuID ||
      product.skuId ||
      item.id ||
      product.id ||
      `product-${index}`,
    productId: firstDefined(
      product.productID,
      product.productId,
      item.productID,
      item.productId,
      fallbackProductId,
      "",
    ),
    productDetailsId: firstDefined(
      product.productDetailsID,
      product.productDetailsId,
      item.productDetailsID,
      item.productDetailsId,
      sku.skuID,
      sku.skuId,
      "",
    ),
    menuItemId: firstDefined(
      product.menuItemID,
      product.menuItemId,
      product.itemID,
      product.itemId,
      item.menuItemID,
      item.menuItemId,
      item.itemID,
      item.itemId,
      "",
    ),
    skuId: firstDefined(
      product.skuID,
      product.skuId,
      item.skuID,
      item.skuId,
      sku.skuID,
      sku.skuId,
      "",
    ),
    name: getCatalogSourceName(item) || "Unnamed item",
    description: firstDefined(
      item.description,
      item.itemDescription,
      item.menuDescription,
      item.productDescription,
      product.description,
      product.itemDescription,
      product.menuDescription,
      product.productDescription,
      basicInfo.description,
      basicInfo.productDescription,
      sku.description,
      sku.technicalSpecs,
      "",
    ),
    ingredients: firstDefined(
      item.ingredients,
      product.ingredients,
      basicInfo.ingredients,
      "",
    ),
    category:
      hasMenuCategoryContext && menuCategoryName
        ? menuCategoryName
        : toCategoryName({ ...item, categoryName, categoryType }),
    productCategory,
    categoryId: firstDefined(
      categoryContext.menuCategoryId,
      categoryContext.menuCategoryID,
      categoryContext.categoryId,
      categoryContext.categoryID,
      item.menuCategoryId,
      item.menuCategoryID,
      product.menuCategoryId,
      product.menuCategoryID,
      item.categoryId,
      item.categoryID,
      product.categoryId,
      product.categoryID,
      basicInfo.categoryId,
      "",
    ),
    menuCategoryId,
    menuCategoryName,
    companyId: firstDefined(
      item.companyId,
      item.companyID,
      product.companyId,
      product.companyID,
      categoryContext.companyId,
      categoryContext.companyID,
      "",
    ),
    categoryType,
    price: toPriceLabel(priceValue, currency),
    priceValue: isUsablePriceValue(priceValue) ? priceValue : "",
    basePrice: Number(
      firstPriceValue(item.basePrice, product.basePrice, priceValue, 0),
    ),
    priceUSD: firstPriceValue(item.priceUSD, product.priceUSD),
    priceLBP: firstPriceValue(item.priceLBP, product.priceLBP),
    currency,
    image: isProductImageRemoved(item)
      ? ""
      : getFirstImageValue(
          images,
          item.productImages,
          product.images,
          product.productImages,
          sku.productImages,
          sku.images,
          item.image,
          item.imageUrl,
          product.image,
          product.imageUrl,
        ),
    portion: firstDefined(
      item.productPortion,
      product.productPortion,
      item.portion,
      item.portionName,
      item.size,
      item.skuSize,
      item.defaultPortion,
      product.portion,
      product.portionName,
      product.size,
      product.skuSize,
      product.defaultPortion,
      sku.portion,
      sku.portionName,
      sku.name,
      sku.displayName,
      sku.size,
      sku.skuSize,
      "",
    ),
    addonSections: getRawAddOnSections(item),
    addOnSections: getRawAddOnSections(item),
    addonSectionIds: Array.isArray(item.addonSectionIds)
      ? item.addonSectionIds
      : [],
    displayOrder: firstDefined(item.displayOrder, product.displayOrder, 0),
    portions: Array.isArray(item.portions) ? item.portions : [],
    rawItem: item,
    active: isActive,
    outOfStock: isOutOfStock,
  };
};

const mapCategoryItem = (categoryItem, index, items) => {
  const name =
    categoryItem.categoryName ||
    categoryItem.name ||
    categoryItem.category ||
    toDisplayLabel(categoryItem.categoryType) ||
    "";
  const protectedType = getProtectedCategoryType(categoryItem);
  const rawMenuCategoryId = firstDefined(
    categoryItem.menuCategoryId,
    categoryItem.menuCategoryID,
    categoryItem.id,
    "",
  );
  const rawCategoryId = firstDefined(
    categoryItem.categoryId,
    categoryItem.categoryID,
    categoryItem.productCategoryId,
    categoryItem.productCategoryID,
    "",
  );
  const menuCategoryId = protectedType
    ? rawMenuCategoryId
    : firstDefined(rawMenuCategoryId, rawCategoryId, "");
  const categoryId = protectedType ? rawCategoryId : "";
  const id = firstDefined(menuCategoryId, categoryId, categoryItem.id, "");
  const itemCount = getCategoryItemCount(categoryItem, items);

  return {
    id: id || `category-${index}`,
    categoryId,
    categoryType: firstDefined(
      categoryItem.categoryType,
      categoryItem.type,
      "",
    ),
    custom: categoryItem.custom,
    name,
    isProtected: Boolean(protectedType),
    menuCategoryId,
    protectedType,
    active: categoryItem.active ?? true,
    companyId: firstDefined(categoryItem.companyId, categoryItem.companyID, ""),
    displayOrder: categoryItem.displayOrder ?? index,
    items: Array.isArray(categoryItem.items) ? categoryItem.items : [],
    itemCount,
    matchedItemCount: itemCount,
    rawCategory: categoryItem,
  };
};

const getCategoryKey = (categoryItem) => {
  const name =
    categoryItem?.categoryName ||
    categoryItem?.name ||
    toDisplayLabel(categoryItem?.categoryType);
  const id =
    categoryItem?.menuCategoryId ||
    categoryItem?.menuCategoryID ||
    categoryItem?.categoryId ||
    categoryItem?.categoryID ||
    categoryItem?.id;

  return String(name || id || "")
    .trim()
    .toLowerCase();
};

const mergeCategoryRows = (...categoryGroups) => {
  const seenKeys = new Set();

  return categoryGroups.flat().filter((categoryItem) => {
    const key = getCategoryKey(categoryItem);
    if (!key || seenKeys.has(key)) return false;

    seenKeys.add(key);
    return true;
  });
};

const getMenuItemProductId = (item = {}) =>
  firstDefined(
    item.productId,
    item.productID,
    item.rawItem?.productId,
    item.rawItem?.productID,
    item.rawItem?.product?.productID,
    item.rawItem?.product?.productId,
    "",
  ) || "";

const getMenuItemId = (item = {}) =>
  firstDefined(
    item.menuItemId,
    item.menuItemID,
    item.itemId,
    item.itemID,
    item.rawItem?.menuItemId,
    item.rawItem?.menuItemID,
    item.rawItem?.itemId,
    item.rawItem?.itemID,
    "",
  ) || "";

const enrichItemsWithProductCategories = async (items, session, companyId) => {
  return items;
};

function VendorMenu() {
  useTranslationSnapshot();
  const [Imp, setImp] = useState(false);
  const [CantDelete, setCantDelete] = useState(false);
  const [importRefresh, setImportRefresh] = useState(0);
  const [Exp, setExp] = useState(false);
  const BType = userService.getUserData().BType;
  const location = useLocation();
  const navigate = useNavigate();
  const isPreview = location.pathname.endsWith("-preview");
  const addItemPath = isPreview
    ? "/vendor-menu-add-preview"
    : "/vendor-menu/add";
  const getEditItemRouteId = (item = {}) =>
    firstDefined(
      item.menuItemId,
      item.menuItemID,
      item.itemId,
      item.itemID,
      item.rawItem?.menuItemId,
      item.rawItem?.menuItemID,
      item.rawItem?.itemId,
      item.rawItem?.itemID,
      item.id,
      "",
    );
  const editItemPath = (item) =>
    isPreview
      ? `/vendor-menu-edit-preview/${encodeURIComponent(getEditItemRouteId(item))}`
      : `/vendor-menu/edit/${encodeURIComponent(getEditItemRouteId(item))}`;
  const [activeTab, setActiveTab] = useState("category");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemPage, setItemPage] = useState(1);
  const [itemPageSize, setItemPageSize] = useState(
    MENU_ITEMS_PAGE_SIZE_OPTIONS[0],
  );
  const [itemPagination, setItemPagination] = useState({
    hasNextPage: false,
    sizePage: MENU_ITEMS_PAGE_SIZE_OPTIONS[0],
    totalElements: null,
    totalPages: 1,
  });
  const [openItemActionId, setOpenItemActionId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [togglingItemId, setTogglingItemId] = useState(null);
  const [itemActionError, setItemActionError] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryApiRows, setCategoryApiRows] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryError, setCategoryError] = useState("");
  const [openCategoryActionId, setOpenCategoryActionId] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [categoryPendingDelete, setCategoryPendingDelete] = useState(null);
  const [categorySubmitError, setCategorySubmitError] = useState("");
  const [addOnSections, setAddOnSections] = useState([]);
  const [addOnSectionsLoading, setAddOnSectionsLoading] = useState(true);
  const [addOnSectionsError, setAddOnSectionsError] = useState("");
  const [showAddOnSectionModal, setShowAddOnSectionModal] = useState(false);
  const [addOnSectionName, setAddOnSectionName] = useState("");
  const [addOnSectionType, setAddOnSectionType] = useState("free");
  const [addOnAllowMultiple, setAddOnAllowMultiple] = useState(false);
  const [addOnAllowedSelections, setAddOnAllowedSelections] = useState("1");
  const [editingAddOnSectionId, setEditingAddOnSectionId] = useState("");
  const [addOnSectionSaving, setAddOnSectionSaving] = useState(false);
  const [addOnSectionSubmitError, setAddOnSectionSubmitError] = useState("");
  const [showAddOnItemModal, setShowAddOnItemModal] = useState(false);
  const [activeAddOnItemSectionId, setActiveAddOnItemSectionId] = useState("");
  const [editingAddOnItemId, setEditingAddOnItemId] = useState("");
  const [addOnItemName, setAddOnItemName] = useState("");
  const [addOnItemPrice, setAddOnItemPrice] = useState("");
  const [addOnItemCurrency, setAddOnItemCurrency] = useState("USD");
  const [addOnItemSaving, setAddOnItemSaving] = useState(false);
  const [addOnItemSubmitError, setAddOnItemSubmitError] = useState("");
  const [deletingAddOnItemId, setDeletingAddOnItemId] = useState("");
  const [deletingAddOnSectionId, setDeletingAddOnSectionId] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const menuItemsRef = useRef([]);

  useEffect(() => {
    if (!openItemActionId) return undefined;

    const handleDocumentMouseDown = (event) => {
      const target = event.target;

      if (
        target instanceof Element &&
        target.closest(".vendor-menu-more-wrapper")
      ) {
        return;
      }

      setOpenItemActionId(null);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [openItemActionId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  useEffect(() => {
    let isMounted = true;

    const loadCategoryData = async (session, companyID) => {
      try {
        if (!userService.getJwt() || !companyID) {
          throw new Error("Missing vendor session.");
        }

        const vendorCategoriesResponse =
          await vendorMenuController.getMenuCategories({
            Authorization: userService.getJwt(),
            companyId: companyID,
            includeItems: true,
          });

        if (!isMounted) return;

        const vendorCategoryRows =
          vendorCategoriesResponse && !isApiError(vendorCategoriesResponse)
            ? getRows(unwrapPayload(vendorCategoriesResponse))
            : [];
        setCategoryApiRows(vendorCategoriesResponse.data);
        const visibleVendorCategoryRows = vendorCategoryRows.filter(
          (row) => !isCategoryRemoved(row),
        );
        const displayCategoryRows = mergeCategoryRows(
          visibleVendorCategoryRows,
          getDefaultCategoryRowsForVendor(),
        );

        if (!isMounted) return;

        if (displayCategoryRows.length > 0) {
          const nextCategories = displayCategoryRows.map(
            (categoryItem, index) =>
              mapCategoryItem(categoryItem, index, menuItemsRef.current),
          );

          setCategories(nextCategories);
        } else {
          setCategories([]);
        }
        if (isApiError(vendorCategoriesResponse)) {
          setCategoryError(
            getErrorMessage(
              vendorCategoriesResponse,
              "Could not load menu categories.",
            ),
          );
        }
      } catch (error) {
        if (!isMounted) return;
        setCategoryApiRows([]);
        setCategories(
          getDefaultCategoryRowsForVendor().map((categoryItem, index) =>
            mapCategoryItem(categoryItem, index, []),
          ),
        );
        setCategoryError(
          getErrorMessage(error, "Could not load menu categories."),
        );
      } finally {
        if (isMounted) {
          setCategoriesLoading(false);
        }
      }
    };

    const fetchCategoryData = async () => {
      if (activeTab !== "category") return;

      setCategoriesLoading(true);
      setCategoryError("");

      try {
        const companyID = userService.getUserData().Compid;

        if (!isMounted) return;

        loadCategoryData(null, companyID);
      } catch (error) {
        if (!isMounted) return;
        setCategoryApiRows([]);
        setCategories(
          getDefaultCategoryRowsForVendor().map((row, index) =>
            mapCategoryItem(row, index, []),
          ),
        );
        setCategoryError(
          getErrorMessage(error, "Could not load menu categories."),
        );
        setCategoriesLoading(false);
      }
    };

    fetchCategoryData();

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;

    const fetchAddOnSections = async () => {
      if (activeTab !== "addons") return;

      setAddOnSectionsLoading(true);
      setAddOnSectionsError("");

      try {
        const companyID = userService.getUserData().Compid;

        if (!userService.getJwt() || !companyID) {
          throw new Error("Missing vendor session.");
        }

        const addOnSectionsResponse =
          await vendorMenuController.getCompanyProductSection({
            Authorization: userService.getJwt(),
            companyId: companyID,
          });

        if (!isMounted) return;

        if (isApiError(addOnSectionsResponse)) {
          throw addOnSectionsResponse;
        }

        const addOnRows = getCompanyAddOnSectionRows(
          unwrapPayload(addOnSectionsResponse),
        );
        setAddOnSections(addOnRows.map(mapAddOnSectionRow));
        setAddOnSectionsError("");
      } catch (error) {
        if (!isMounted) return;
        setAddOnSections([]);
        setAddOnSectionsError(
          getErrorMessage(error, "Could not load add-on sections."),
        );
      } finally {
        if (isMounted) {
          setAddOnSectionsLoading(false);
        }
      }
    };

    fetchAddOnSections();

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;

    const fetchMenuItems = async () => {
      if (activeTab !== "items") return;

      const shouldSkipShortSearch =
        debouncedSearch.length > 0 && debouncedSearch.length <= 2;

      if (shouldSkipShortSearch) {
        setItemsLoading(false);
        return;
      }

      setItemsLoading(true);
      setItemActionError("");
      setOpenItemActionId(null);

      try {
        const companyID = userService.getUserData().Compid;

        if (!userService.getJwt() || !companyID) {
          throw new Error("Missing vendor session.");
        }

        const catalogResponse = await vendorMenuController.getItems({
          Authorization: userService.getJwt(),
          companyId: companyID,
          page: itemPage,
          search: debouncedSearch || undefined,
          size: itemPageSize,
        });

        if (!isMounted) return;

        if (isApiError(catalogResponse)) {
          throw catalogResponse;
        }

        const catalogPayload = unwrapPayload(catalogResponse);
        const catalogRows = getRows(catalogPayload);
        const catalogItems = catalogRows.map(mapCatalogItem);
        const visibleVendorCategoryRows = [];
        const mergedItems = applyCategoryNamesToItems(
          catalogItems,
          visibleVendorCategoryRows,
        );
        const nextItems = clearRemovedCategoryDisplayFromItems(
          await enrichItemsWithProductCategories(mergedItems, null, companyID),
        );

        if (!isMounted) return;

        menuItemsRef.current = nextItems;
        setMenuItems(nextItems);
        setItemPagination(
          getCatalogPagination(
            catalogPayload,
            catalogRows.length,
            itemPage,
            itemPageSize,
          ),
        );
      } catch (error) {
        if (!isMounted) return;

        menuItemsRef.current = [];
        setMenuItems([]);
        setItemPagination({
          hasNextPage: false,
          sizePage: itemPageSize,
          totalElements: null,
          totalPages: 1,
        });
        setItemActionError(
          getErrorMessage(error, "Could not load menu items."),
        );
      } finally {
        if (isMounted) {
          setItemsLoading(false);
        }
      }
    };

    fetchMenuItems();

    return () => {
      isMounted = false;
    };
  }, [activeTab, debouncedSearch, itemPage, itemPageSize, importRefresh]);

  const visibleItems = menuItems;

  // const toggleItem = async (item) => {
  //   if (!item || togglingItemId) return;

  //   let itemId = getMenuItemId(item);
  //   const stateItemId = item.id;
  //   const nextActive = !item.active;

  //   const previousItems = menuItemsRef.current;
  //   const optimisticItems = previousItems.map((currentItem) =>
  //     currentItem.id === stateItemId
  //       ? {
  //           ...currentItem,
  //           active: nextActive,
  //           outOfStock: nextActive ? false : true,
  //         }
  //       : currentItem,
  //   );

  //   menuItemsRef.current = optimisticItems;
  //   setMenuItems(optimisticItems);
  //   setTogglingItemId(stateItemId);
  //   setItemActionError("");

  //   try {
  //       //     const companyId = firstDefined(
  //       item.companyId,
  //       item.rawItem?.companyId,
  //       item.rawItem?.companyID,
  //       userService.getUserData().Compid,
  //       userService.getUserData().Compid,
  //     );

  //     if (!userService.getJwt() || !companyId) {
  //       throw new Error("Missing vendor session.");
  //     }

  //     let resolvedItem = item;

  //     if (!itemId) {
  //       const vendorSnapshot = await fetchVendorMenuSnapshot({
  //         companyId,
  //         session,
  //       });
  //       const hydratedItems = vendorSnapshot.hasVendorData
  //         ? hydrateMenuItemsWithVendorRows([item], vendorSnapshot.vendorRows)
  //         : [];
  //       const hydratedItem =
  //         findMatchingMenuItem(hydratedItems, item) || hydratedItems[0];

  //       itemId = getMenuItemId(hydratedItem);

  //       if (itemId && hydratedItem) {
  //         resolvedItem = mergeMenuItem(item, hydratedItem);
  //         const hydratedStateItems = menuItemsRef.current.map((currentItem) =>
  //           currentItem.id === stateItemId
  //             ? {
  //                 ...mergeMenuItem(currentItem, resolvedItem),
  //                 active: nextActive,
  //                 outOfStock: nextActive ? false : true,
  //               }
  //             : currentItem,
  //         );

  //         menuItemsRef.current = hydratedStateItems;
  //         setMenuItems(hydratedStateItems);
  //       }
  //     }

  //     if (!itemId) {
  //       const productResult = await updateProductOnlyItemActive({
  //         active: nextActive,
  //         companyId,
  //         item,
  //         session,
  //       });
  //       const confirmedItems = menuItemsRef.current.map((currentItem) =>
  //         currentItem.id === stateItemId
  //           ? {
  //               ...currentItem,
  //               active: productResult.active,
  //               outOfStock: productResult.active ? false : true,
  //               productId: productResult.productId || currentItem.productId,
  //             }
  //           : currentItem,
  //       );

  //       menuItemsRef.current = confirmedItems;
  //       setMenuItems(confirmedItems);
  //       setItemStatusOverride(
  //         { ...item, productId: productResult.productId || item.productId },
  //         {
  //           active: productResult.active,
  //           outOfStock: productResult.active ? false : true,
  //         },
  //       );

  //       return;
  //     }

  //     const response = await vendorMenuController.updateItemActive({
  //       active: nextActive,
  //       Authorization: userService.getJwt(),
  //       companyId,
  //       itemId,
  //     });

  //     if (isApiError(response)) {
  //       throw response;
  //     }

  //     const payload = unwrapPayload(response);
  //     const hasPayload = isMenuItemPayload(payload);
  //     const responseItem = hasPayload
  //       ? mapVendorMenuRowToItem(
  //           {
  //             ...payload,
  //             active: firstDefined(payload.active, nextActive),
  //             categoryName: firstDefined(
  //               payload.categoryName,
  //               resolvedItem.categoryName,
  //               resolvedItem.menuCategoryName,
  //               resolvedItem.category,
  //             ),
  //             itemId: firstDefined(
  //               payload.itemId,
  //               payload.itemID,
  //               payload.menuItemId,
  //               payload.menuItemID,
  //               itemId,
  //             ),
  //             menuCategoryId: firstDefined(
  //               payload.menuCategoryId,
  //               payload.menuCategoryID,
  //               payload.categoryId,
  //               resolvedItem.menuCategoryId,
  //               resolvedItem.categoryId,
  //             ),
  //             menuItemId: firstDefined(
  //               payload.menuItemId,
  //               payload.menuItemID,
  //               payload.itemId,
  //               payload.itemID,
  //               itemId,
  //             ),
  //           },
  //           `active-${itemId}`,
  //         )
  //       : null;

  //     const confirmedItems = menuItemsRef.current.map((currentItem) => {
  //       if (currentItem.id !== stateItemId) return currentItem;

  //       const mergedItem = responseItem
  //         ? mergeMenuItem(currentItem, responseItem)
  //         : currentItem;
  //       const responseActive = toOptionalBoolean(
  //         firstDefined(responseItem?.active, payload?.active, nextActive),
  //       );
  //       const confirmedActive =
  //         responseActive !== undefined ? responseActive : nextActive;
  //       const responseOutOfStock = toOptionalBoolean(
  //         firstDefined(responseItem?.outOfStock, payload?.outOfStock),
  //       );

  //       return {
  //         ...mergedItem,
  //         menuItemId: firstDefined(mergedItem.menuItemId, itemId, ""),
  //         active: confirmedActive,
  //         outOfStock: confirmedActive ? responseOutOfStock === true : true,
  //       };
  //     });

  //     menuItemsRef.current = confirmedItems;
  //     setMenuItems(confirmedItems);
  //     const confirmedItem =
  //       confirmedItems.find((currentItem) => currentItem.id === stateItemId) ||
  //       null;

  //     if (confirmedItem) {
  //       setItemStatusOverride(confirmedItem, {
  //         active: confirmedItem.active,
  //         outOfStock: confirmedItem.outOfStock,
  //       });
  //     }
  //   } catch (error) {
  //     menuItemsRef.current = previousItems;
  //     setMenuItems(previousItems);

  //     setItemActionError(
  //       getErrorMessage(error, "Could not update menu item status."),
  //     );
  //   } finally {
  //     setTogglingItemId(null);
  //   }
  // };

  const toggleItem = async (item) => {
    if (!item || togglingItemId) return;

    const stateItemId = item.id;
    const requestedActive = !item.active;

    try {
      setTogglingItemId(stateItemId);
      setItemActionError("");

      const response = await vendorMenuController.updateItemActive({
        Authorization: userService.getJwt(),
        active: requestedActive,
        companyId: item.companyId,
        itemId: item.menuItemId,
      });
      if (isApiError(response)) {
        throw response;
      }

      const payload = unwrapPayload(response);
      const responseActive = toOptionalBoolean(
        firstDefined(payload?.active, payload?.isActive, payload?.enabled),
      );
      const confirmedActive =
        responseActive !== undefined ? responseActive : requestedActive;
      const confirmedOutOfStock = !confirmedActive;
      const stockResponse = await vendorMenuController.OutOfStock({
        Authorization: userService.getJwt(),
        outOfStock: confirmedOutOfStock,
        companyId: item.companyId,
        itemId: item.menuItemId,
      });
      if (isApiError(stockResponse)) {
        throw stockResponse;
      }

      const confirmedItems = menuItemsRef.current.map((currentItem) =>
        currentItem.id === stateItemId
          ? {
              ...currentItem,
              ...(payload && typeof payload === "object" ? payload : {}),
              active: confirmedActive,
              outOfStock: confirmedOutOfStock,
            }
          : currentItem,
      );

      menuItemsRef.current = confirmedItems;
      setMenuItems(confirmedItems);
    } catch (error) {
      setItemActionError(
        getErrorMessage(error, "Could not update menu item status."),
      );
    } finally {
      setTogglingItemId(null);
    }
  };

  const openCategoryModal = (categoryItem = null) => {
    if (
      categoryItem &&
      !isGroceryVendorPortal() &&
      isProtectedCategory(categoryItem)
    ) {
      return;
    }

    setEditingCategoryId(
      categoryItem ? String(getCategoryEditId(categoryItem) || "") : null,
    );
    setCategoryName(getCategoryRowName(categoryItem) || "");
    setCategorySubmitError("");
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategoryId(null);
    setCategoryName("");
    setCategorySubmitError("");
  };

  const openAddOnSectionModal = (section = null) => {
    const allowMultiple = Boolean(section?.allowMultiple);

    setEditingAddOnSectionId(section?.id || "");
    setAddOnSectionName(section?.name || "");
    setAddOnSectionType(section?.pricingType || "free");
    setAddOnAllowMultiple(allowMultiple);
    setAddOnAllowedSelections(
      String(firstDefined(section?.maxSelections, allowMultiple ? "1" : "1")),
    );
    setAddOnSectionSubmitError("");
    setShowAddOnSectionModal(true);
  };

  const closeAddOnSectionModal = (force = false) => {
    if (addOnSectionSaving && !force) return;

    setShowAddOnSectionModal(false);
    setAddOnSectionName("");
    setAddOnSectionType("free");
    setAddOnAllowMultiple(false);
    setAddOnAllowedSelections("1");
    setEditingAddOnSectionId("");
    setAddOnSectionSubmitError("");
  };

  const saveAddOnSection = async (event) => {
    event.preventDefault();

    if (addOnSectionSaving) return;

    const title = addOnSectionName.trim();
    if (!title) {
      setAddOnSectionSubmitError("Add-on section name is required.");
      return;
    }

    try {
      setAddOnSectionSaving(true);
      setAddOnSectionSubmitError("");
      setAddOnSectionsError("");

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const maxSelectionNumber = normalizeAddOnSelectionLimit(
        addOnAllowedSelections,
        addOnAllowMultiple,
      );
      const editingSection = editingAddOnSectionId
        ? addOnSections.find(
            (section) => String(section.id) === String(editingAddOnSectionId),
          )
        : null;
      const editingSectionBackendId =
        editingSection?.addonSectionId ||
        getAddOnSectionBackendId(editingSection?.rawSection || {});
      const request = {
        active: true,
        allowsMultipleSelections: addOnAllowMultiple,
        maxSelectionNumber,
        pricingType: addOnSectionType,
        selectionType: true,
        paid: addOnSectionType === "free" ? false : true,
        title,
        type: addOnSectionType,
      };
      if (!editingSectionBackendId) {
        request.additions = [];
      }
      if (editingSectionBackendId) {
        request.productAddOnSectionId = editingSectionBackendId;
      }

      const response = await vendorMenuController.saveAddonSection({
        Authorization: userService.getJwt(),
        companyId,
        request,
      });

      if (isApiError(response)) {
        throw response;
      }

      const payload = unwrapPayload(response);
      const responseRows = getCompanyAddOnSectionRows(payload);
      const nextSections =
        responseRows.length > 0
          ? responseRows.map(mapAddOnSectionRow)
          : [
              mapAddOnSectionRow(
                {
                  ...request,
                  ...(payload && typeof payload === "object" ? payload : {}),
                  name: title,
                  title,
                },
                addOnSections.length,
              ),
            ];

      setAddOnSections((currentSections) => {
        const sectionById = new Map(
          currentSections.map((section) => [String(section.id), section]),
        );

        nextSections.forEach((section) => {
          const existingSection =
            sectionById.get(String(section.id)) ||
            (editingAddOnSectionId
              ? sectionById.get(String(editingAddOnSectionId))
              : null);

          sectionById.set(String(section.id), {
            ...(existingSection || {}),
            ...section,
            items:
              section.items.length > 0
                ? section.items
                : existingSection?.items || [],
          });
          if (
            editingAddOnSectionId &&
            String(section.id) !== String(editingAddOnSectionId)
          ) {
            sectionById.delete(String(editingAddOnSectionId));
          }
        });

        return Array.from(sectionById.values());
      });
      closeAddOnSectionModal(true);
      setActiveTab("addons");
    } catch (error) {
      setAddOnSectionSubmitError(
        getErrorMessage(error, "Could not save add-on section."),
      );
    } finally {
      setAddOnSectionSaving(false);
    }
  };

  const openAddOnItemModal = (section, item = null) => {
    setActiveAddOnItemSectionId(section?.id || "");
    setEditingAddOnItemId(item ? getAddOnItemBackendId(item) : "");
    setAddOnItemName(item ? getAddOnItemName(item) : "");
    setAddOnItemPrice(item ? String(getAddOnItemPrice(item)) : "");
    setAddOnItemCurrency(item ? getAddOnItemCurrency(item) : "USD");
    setAddOnItemSubmitError("");
    setShowAddOnItemModal(true);
  };

  const closeAddOnItemModal = (force = false) => {
    if (addOnItemSaving && !force) return;

    setShowAddOnItemModal(false);
    setActiveAddOnItemSectionId("");
    setEditingAddOnItemId("");
    setAddOnItemName("");
    setAddOnItemPrice("");
    setAddOnItemCurrency("USD");
    setAddOnItemSubmitError("");
  };

  const refreshAddOnSections = async ({ Authorization, companyId }) => {
    const response = await vendorMenuController.getCompanyProductSection({
      Authorization,
      companyId,
    });

    if (isApiError(response)) {
      throw response;
    }

    const addOnRows = getCompanyAddOnSectionRows(unwrapPayload(response));
    setAddOnSections(addOnRows.map(mapAddOnSectionRow));
    setAddOnSectionsError("");
  };

  const saveAddOnItem = async (event) => {
    event.preventDefault();

    if (addOnItemSaving) return;

    const targetSection = addOnSections.find(
      (section) => String(section.id) === String(activeAddOnItemSectionId),
    );
    const addonSectionId =
      targetSection?.addonSectionId ||
      getAddOnSectionBackendId(targetSection?.rawSection || {});
    const name = addOnItemName.trim();

    if (!addonSectionId) {
      setAddOnItemSubmitError("Missing add-on section id.");
      return;
    }
    if (!name) {
      setAddOnItemSubmitError("Add-on item name is required.");
      return;
    }

    const isPaidSection = targetSection?.pricingType === "paid";
    const itemPrice = isPaidSection
      ? normalizeAddOnItemPrice(addOnItemPrice)
      : 0;

    if (isPaidSection && itemPrice <= 0) {
      setAddOnItemSubmitError("Add-on item price is required.");
      return;
    }

    try {
      setAddOnItemSaving(true);
      setAddOnItemSubmitError("");
      setAddOnSectionsError("");

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const request = {
        active: true,
        currency: addOnItemCurrency,
        name,
        price: itemPrice,
      };
      if (editingAddOnItemId) {
        request.addonItemId = editingAddOnItemId;
      }

      const response = await vendorMenuController.saveAddonItem({
        Authorization: userService.getJwt(),
        addonSectionId,
        companyId,
        request,
      });

      if (isApiError(response)) {
        throw response;
      }

      await refreshAddOnSections({
        Authorization: userService.getJwt(),
        companyId,
      });
      closeAddOnItemModal(true);
    } catch (error) {
      setAddOnItemSubmitError(
        getErrorMessage(error, "Could not save add-on item."),
      );
    } finally {
      setAddOnItemSaving(false);
    }
  };

  const deleteAddOnItem = async (section, item) => {
    const addonItemId = getAddOnItemBackendId(item);

    if (!addonItemId || deletingAddOnItemId) return;

    try {
      setDeletingAddOnItemId(addonItemId);
      setAddOnSectionsError("");

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const response = await vendorMenuController.deleteAddonItem({
        Authorization: userService.getJwt(),
        addonItemId,
        companyId,
      });

      if (isApiError(response)) {
        throw response;
      }

      setAddOnSections((currentSections) =>
        currentSections.map((currentSection) => {
          if (String(currentSection.id) !== String(section.id)) {
            return currentSection;
          }

          const nextItems = (currentSection.items || []).filter(
            (currentItem) =>
              String(getAddOnItemBackendId(currentItem)) !==
              String(addonItemId),
          );

          return {
            ...currentSection,
            items: nextItems,
            rawSection: {
              ...(currentSection.rawSection || {}),
              additions: nextItems,
              items: nextItems,
            },
          };
        }),
      );
    } catch (error) {
      setAddOnSectionsError(
        getErrorMessage(error, "Could not delete add-on item."),
      );
    } finally {
      setDeletingAddOnItemId("");
    }
  };

  const deleteAddOnSection = async (section) => {
    const addonSectionId =
      section?.addonSectionId ||
      getAddOnSectionBackendId(section?.rawSection || {});

    if (!addonSectionId || deletingAddOnSectionId) return;

    try {
      setDeletingAddOnSectionId(addonSectionId);
      setAddOnSectionsError("");

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const response = await vendorMenuController.deleteAddonSection({
        Authorization: userService.getJwt(),
        addonSectionId,
        companyId,
      });

      if (isApiError(response)) {
        throw response;
      }

      setAddOnSections((currentSections) =>
        currentSections.filter(
          (currentSection) => String(currentSection.id) !== String(section.id),
        ),
      );
    } catch (error) {
      setAddOnSectionsError(
        getErrorMessage(error, "Could not delete add-on section."),
      );
    } finally {
      setDeletingAddOnSectionId("");
    }
  };

  const saveCategory = async (event) => {
    event.preventDefault();

    if (categorySaving) {
      console.log("[Vendor Menu] category submit blocked: already saving");
      return;
    }

    const name = categoryName.trim();
    if (!name) {
      console.log("[Vendor Menu] category submit blocked: missing name");
      setCategorySubmitError("Category name is required.");
      return;
    }

    const isEditMode = Boolean(editingCategoryId);
    const editingCategoryFromState = isEditMode
      ? categories.find((categoryItem) =>
          categoryHasId(categoryItem, editingCategoryId),
        )
      : null;
    const editingCategoryFromApi = isEditMode
      ? categoryApiRows.find((categoryItem) =>
          categoryHasId(categoryItem, editingCategoryId),
        )
      : null;
    const editingCategory =
      editingCategoryFromState ||
      (editingCategoryFromApi
        ? mapCategoryItem(
            editingCategoryFromApi,
            categoryApiRows.findIndex((categoryItem) =>
              categoryHasId(categoryItem, editingCategoryId),
            ),
            menuItems,
          )
        : null);

    if (
      editingCategory &&
      !isGroceryVendorPortal() &&
      isProtectedCategory(editingCategory)
    ) {
      closeCategoryModal();
      return;
    }

    try {
      setCategorySaving(true);
      setCategorySubmitError("");

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const editingCategoryIndex = editingCategory
        ? categories.findIndex((categoryItem) =>
            categoryHasId(categoryItem, editingCategoryId),
          )
        : -1;
      const editingMenuCategoryId = firstDefined(
        editingCategory?.menuCategoryId,
        editingCategory?.id,
        editingCategoryFromApi?.menuCategoryId,
        editingCategoryFromApi?.menuCategoryID,
        "",
      );
      const request = {
        active: true,
        custom: true,
        ...(editingCategory?.categoryId
          ? { categoryId: editingCategory.categoryId }
          : {}),
        ...(editingCategory?.categoryType
          ? { categoryType: editingCategory.categoryType }
          : {}),
        companyId,
        displayOrder:
          isEditMode && editingCategoryIndex >= 0
            ? editingCategoryIndex
            : categories.length,
        items: [],
        ...(isEditMode && editingMenuCategoryId
          ? { menuCategoryId: editingMenuCategoryId }
          : {}),
        name,
      };

      const response =
        isEditMode && editingMenuCategoryId
          ? await vendorMenuController.saveCategory({
              Authorization: userService.getJwt(),
              companyId,
              request,
            })
          : await vendorMenuController.addCategory({
              Authorization: userService.getJwt(),
              companyId,
              request,
            });

      if (isApiError(response)) {
        throw response;
      }

      const payload = unwrapPayload(response);
      const savedSource =
        payload?.name || payload?.menuCategoryId || payload?.categoryId
          ? {
              ...request,
              ...payload,
              categoryName: firstDefined(
                payload.categoryName,
                payload.name,
                name,
              ),
              menuCategoryId: firstDefined(
                payload.menuCategoryId,
                payload.menuCategoryID,
                editingMenuCategoryId,
              ),
            }
          : request;
      const savedCategory = mapCategoryItem(
        savedSource,
        isEditMode && editingCategoryIndex >= 0
          ? editingCategoryIndex
          : categories.length,
        menuItems,
      );
      setCategoryRemoved(savedCategory, false);
      let itemsForCategoryDisplay = menuItemsRef.current;

      if (isEditMode && editingCategory) {
        const nextItems = syncSavedCategoryDisplayOnItems(
          menuItemsRef.current,
          editingCategory,
          savedCategory,
        );
        menuItemsRef.current = nextItems;
        setMenuItems(nextItems);
        itemsForCategoryDisplay = nextItems;
      }

      setCategories((currentCategories) => {
        if (
          isEditMode &&
          editingCategory &&
          currentCategories.some(
            (categoryItem) => categoryItem.id === editingCategory.id,
          )
        ) {
          return currentCategories.map((categoryItem) =>
            categoryItem.id === editingCategory.id
              ? {
                  ...savedCategory,
                  id: savedCategory.id || editingCategory.id,
                  itemCount: categoryItem.itemCount,
                  matchedItemCount: categoryItem.matchedItemCount,
                }
              : categoryItem,
          );
        }

        if (
          savedCategory.id &&
          currentCategories.some(
            (categoryItem) => categoryItem.id === savedCategory.id,
          )
        ) {
          return currentCategories.map((categoryItem) =>
            categoryItem.id === savedCategory.id ? savedCategory : categoryItem,
          );
        }

        return [...currentCategories, savedCategory];
      });

      setCategoryApiRows((currentRows) => {
        if (!isEditMode || !editingMenuCategoryId) return currentRows;

        return currentRows.map((categoryItem) =>
          categoryHasId(categoryItem, editingMenuCategoryId)
            ? { ...categoryItem, ...savedSource }
            : categoryItem,
        );
      });

      try {
        const refreshedCategoriesResponse =
          await vendorMenuController.getCategories({
            Authorization: userService.getJwt(),
            companyId,
            includeItems: true,
          });

        if (!isApiError(refreshedCategoriesResponse)) {
          const refreshedApiRows = getRows(
            unwrapPayload(refreshedCategoriesResponse),
          );
          setCategoryApiRows(refreshedApiRows);
          const refreshedRows = mergeCategoryRows(
            refreshedApiRows,
            savedSource,
            getDefaultCategoryRowsForVendor(),
          ).filter((categoryItem) => !isCategoryRemoved(categoryItem));

          if (refreshedRows.length > 0) {
            setCategories(
              refreshedRows.map((categoryItem, index) =>
                mapCategoryItem(categoryItem, index, itemsForCategoryDisplay),
              ),
            );
          }
        }
      } catch (refreshError) {
        console.log(
          "[Vendor Menu] category refresh after save error",
          refreshError,
        );
      }

      setCategoryError("");
      closeCategoryModal();
    } catch (error) {
      setCategorySubmitError(
        getErrorMessage(
          error,
          isEditMode
            ? "Could not update category. Check fields and try again."
            : "Could not add category. Check fields and try again.",
        ),
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const requestDeleteCategory = (categoryItem) => {
    if (!isGroceryVendorPortal() && isProtectedCategory(categoryItem)) return;
    setOpenCategoryActionId(null);
    if (categoryItem.itemsCount > 0) {
      setCantDelete(true);
      return;
    }
    setCategoryPendingDelete(categoryItem);
  };

  const closeDeleteCategoryDialog = () => {
    if (deletingCategoryId) return;
    setCategoryPendingDelete(null);
  };

  const deleteCategory = async (categoryItem = categoryPendingDelete) => {
    if (categoryItem?.itemsCount > 0) {
      setCategoryPendingDelete(null);
      setCantDelete(true);
      return;
    }
    if (!isGroceryVendorPortal() && isProtectedCategory(categoryItem)) {
      return;
    }

    const categoryId = getDeletableCategoryId(categoryItem);

    if (!categoryId) {
      setCategoryError("Could not delete category: missing category id.");
      return;
    }

    if (deletingCategoryId) return;

    try {
      setDeletingCategoryId(categoryId);
      setCategoryError("");

      const companyId = firstDefined(
        categoryItem?.companyId,
        categoryItem?.rawCategory?.companyId,
        categoryItem?.rawCategory?.companyID,
        userService.getUserData().Compid,
        userService.getUserData().Compid,
      );

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const response = await vendorMenuController.deleteCategory({
        Authorization: userService.getJwt(),
        categoryId,
        companyId,
      });

      if (isApiError(response)) {
        throw response;
      }

      setCategoryRemoved(categoryItem, true);
      setCategoryApiRows((currentRows) =>
        currentRows.filter(
          (currentRow) =>
            !getCategoryRemovalKeys(categoryItem).some((key) =>
              getCategoryRemovalKeys(currentRow).includes(key),
            ),
        ),
      );
      setCategories((currentCategories) =>
        currentCategories.filter(
          (currentCategory) =>
            !getCategoryRemovalKeys(categoryItem).some((key) =>
              getCategoryRemovalKeys(currentCategory).includes(key),
            ),
        ),
      );
      const nextItems = clearRemovedCategoryDisplayFromItems(
        menuItemsRef.current,
      );
      menuItemsRef.current = nextItems;
      setMenuItems(nextItems);
      setCategoryPendingDelete(null);
    } catch (error) {
      console.log("[Vendor Menu] deleteCategory error", {
        categoryId,
        categoryItem,
        error,
      });
      setCategoryError(
        getErrorMessage(error, "Could not delete menu category."),
      );
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const closePreviewImage = () => {
    setPreviewImage(null);
  };

  const editMenuItem = (item) => {
    setOpenItemActionId(null);
    navigate(editItemPath(item), {
      state: {
        item,
        mode: "edit",
      },
    });
  };

  const deleteMenuItem = async (item) => {
    if (!item || deletingItemId) return;

    try {
      setDeletingItemId(item.id);
      setItemActionError("");

      const companyId = firstDefined(
        item.companyId,
        item.rawItem?.companyId,
        item.rawItem?.companyID,
        userService.getUserData().Compid,
        userService.getUserData().Compid,
      );

      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }

      const itemId = getMenuItemId(item);
      const productId = getMenuItemProductId(item);

      if (!itemId && !productId) {
        throw new Error("Missing item id.");
      }

      let response = null;

      if (itemId) {
        response = await vendorMenuController.deleteItem({
          Authorization: userService.getJwt(),
          companyId,
          itemId,
        });
        //   response = await productController.deleteProductByProductId({
        //     Authorization: userService.getJwt(),
        //     productId,
        //   });
        // } else if (productId) {
        //   response = await productController.deleteProductByProductId({
        //     Authorization: userService.getJwt(),
        //     productId,
        //   });
      }

      if (response && isApiError(response)) {
        throw response;
      }

      const nextItems = menuItemsRef.current.filter(
        (currentItem) => currentItem.id !== item.id,
      );
      menuItemsRef.current = nextItems;
      setMenuItems(nextItems);
      setCategories((currentCategories) =>
        currentCategories.map((categoryItem, index) =>
          mapCategoryItem(
            categoryItem.rawCategory || categoryItem,
            index,
            nextItems,
          ),
        ),
      );

      setOpenItemActionId(null);
    } catch (error) {
      console.log("[Vendor Menu] delete item error", {
        item,
        error,
      });
      setItemActionError(getErrorMessage(error, "Could not delete menu item."));
    } finally {
      setDeletingItemId(null);
    }
  };
  return (
    <VendorLayout activeKey="menu">
      <section className="vendor-menu-page">
        <div className="vendor-menu-page-header">
          <div className="vendor-menu-heading">
            <div>
              <div className="vendor-menu-page-title">{translateLabel("txt_products_menu", "Products & Menu")}</div>
              <div className="vendor-menu-page-subtitle">
                {translateLabel("txt_products_menu_description", "Manage your catalog, categories, and add-ons")}
              </div>
            </div>
          </div>

          <span className="vendor-store-online">
            <span aria-hidden="true" /> {translateLabel("txt_store_online", "Store Online")} </span>
        </div>

        {activeTab === "items" ? (
          <div className="vendor-menu-section-header">
            <div>
              <div className="vendor-menu-section-title">{translateLabel("txt_menu_item", "Menu Item")}</div>
            </div>

            <div className="vendor-menu-items-management-buttons">
              <div className="vendor-menu-items-import-export">
                <button
                  type="button"
                  className="vendor-add-item-button"
                  onClick={() => setImp(true)}
                >
                  <DownArrowImpIcon />
                  {translateLabel("txt_web_vendor_import", "Import")}
                </button>
                <button
                  type="button"
                  className="vendor-add-item-button"
                  onClick={() => setExp(true)}
                >
                  <ArrowUpExpIcon />
                  {translateLabel("txt_web_vendor_export", "Export")}
                </button>
              </div>

              <Link className="vendor-add-item-button" to={addItemPath}>
                <LuPlus aria-hidden="true" /> {translateLabel("txt_web_vendor_add_item", "Add Item")} </Link>
            </div>
          </div>
        ) : activeTab === "addons" ? (
          <div className="vendor-menu-section-header">
            <div>
              <div className="vendor-menu-section-title">{translateLabel("txt_web_vendor_add_ons", "Add-ons")}</div>
              <div className="vendor-menu-section-subtitle">
                {translateLabel("txt_web_vendor_manage_reusable_modifier_sections_for_your_products", "Manage reusable modifier sections for your products.")}
              </div>
            </div>
            <button
              className="vendor-add-item-button"
              type="button"
              onClick={openAddOnSectionModal}
            >
              <LuPlus aria-hidden="true" /> {translateLabel("txt_addons_section", "Add-ons Section")} </button>
          </div>
        ) : (
          <div className="vendor-category-add-row">
            <button type="button" onClick={() => openCategoryModal()}>
              <LuPlus aria-hidden="true" /> {translateLabel("txt_web_vendor_add_categories", "Add Categories")} </button>
          </div>
        )}

        <div
          className="vendor-menu-tabs"
          style={{ "--tab-count": BType === "GROCERIES" ? 2 : 3 }}
          role="tablist"
        >
          <button
            className={activeTab === "category" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={activeTab === "category"}
            onClick={() => setActiveTab("category")}
          > {translateLabel("btn_category", "Category")} </button>
          <button
            className={activeTab === "items" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={activeTab === "items"}
            onClick={() => setActiveTab("items")}
          >
            {translateLabel("txt_web_vendor_items", "Items")}
          </button>
          {BType === "GROCERIES" ? null : (
            <button
              className={activeTab === "addons" ? "active" : ""}
              type="button"
              role="tab"
              aria-selected={activeTab === "addons"}
              onClick={() => setActiveTab("addons")}
            >
              {translateLabel("txt_web_vendor_add_ons", "Add-ons")}
            </button>
          )}
        </div>

        {activeTab === "items" && (
          <label className="vendor-menu-search">
            <LuSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder={translateLabel("input_search_items", "Search items...")}
              aria-label={translateLabel("txt_web_vendor_search_menu_items", "Search menu items")}
              onChange={(event) => {
                setSearch(event.target.value);
                setItemPage(1);
              }}
            />
          </label>
        )}

        {activeTab === "category" ? (
          <section className="vendor-category-section">
            <div className="vendor-category-section-header">
              <div>
                <div className="vendor-category-section-title">
                  {translateLabel("txt_menu_sections", "Menu Sections")}
                </div>
              </div>
              <button
                className="vendor-category-info-button"
                type="button"
                aria-label={translateLabel("txt_web_vendor_menu_sections_information", "Menu sections information")}
              >
                <InfoGrayIcon />
                <span role="tooltip"> {translateLabel("txt_web_vendor_menu_sections_group_your_items_and_control_how_categories_appear_to_customers", "Menu sections group your items and control how categories appear to customers.")} </span>
              </button>
            </div>

            <div className="vendor-category-list">
              {categoryError && (
                <div className="vendor-category-list-feedback" role="alert">
                  {translateMenuFeedback(categoryError)}
                </div>
              )}

              {categoriesLoading && (
                <div className="vendor-menu-empty-state">
                  {translateLabel("txt_web_vendor_loading_menu_categories", "Loading menu categories...")}
                </div>
              )}

              {/* {!categoriesLoading &&
                categoryApiRows.map((menuCategoryId, index) => {
                  const categoryItem =
                    categories.find(
                      (currentCategory) =>
                        getCategoryApiCardKey(
                          currentCategory.rawCategory || currentCategory,
                          index,
                        ) === menuCategoryId,
                    ) || mapCategoryItem(menuCategoryId, index, menuItems);
                  const categoryProtected =
                    !isGroceryVendorPortal() &&
                    isProtectedCategory(categoryItem);
                  const isActive = getCategoryApiActive(menuCategoryId);

                  return (
                    <article
                      className="vendor-category-card"
                      key={menuCategoryId}
                    >
                      <div className="vendor-category-info">
                        <div className="vendor-category-name">
                          {menuCategoryId.name}
                        </div>
                        <div className="vendor-category-meta">
                          <span>{menuCategoryId?.itemsCount} items</span>
                          <span
                            className={
                              isActive
                                ? "vendor-menu-status-active"
                                : "vendor-menu-status-inactive"
                            }
                          >
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div
                        className={`vendor-category-actions ${
                          categoryProtected
                            ? "vendor-category-actions-protected"
                            : ""
                        }`}
                      >
                        {!categoryProtected && (
                          <div className="vendor-category-action-menu">
                            {isActive ? (
                              <button
                                className="vendor-category-edit-button"
                                type="button"
                                aria-expanded={
                                  openCategoryActionId === categoryItem.id
                                }
                                aria-label={`Open actions for ${categoryItem.name}`}
                                onClick={() =>
                                  setOpenCategoryActionId((currentId) =>
                                    currentId === categoryItem.id
                                      ? null
                                      : categoryItem.id,
                                  )
                                }
                              >
                                <span>Edit</span>
                              </button>
                            ) : null}

                            {openCategoryActionId === categoryItem.id && (
                              <div className="vendor-category-action-dropdown">
                                <button
                                  type="button"
                                  aria-label={`Edit ${categoryItem.name}`}
                                  onClick={() => {
                                    setOpenCategoryActionId(null);
                                    openCategoryModal(categoryItem);
                                  }}
                                >
                                  <EditGrayIcon />
                                  <span>Edit</span>
                                </button>

                                <button
                                  type="button"
                                  aria-label={`Delete ${categoryItem.name}`}
                                  disabled={
                                    !getDeletableCategoryId(categoryItem) ||
                                    deletingCategoryId ===
                                      getDeletableCategoryId(categoryItem)
                                  }
                                  onClick={() => {
                                    setOpenCategoryActionId(null);
                                    requestDeleteCategory(categoryItem);
                                  }}
                                >
                                  <TrachRedIcon />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })} */}
              {/*/**cccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccc */}
              {!categoriesLoading &&
                categoryApiRows.map((categoryItem) => {
                  const isActive = categoryItem.active;
                  const categoryProtected = categoryItem.custom === false;

                  return (
                    <article
                      className="vendor-category-card"
                      key={categoryItem.menuCategoryId}
                    >
                      <div className="vendor-category-info">
                        <div className="vendor-category-name">
                          {translateRestaurantCategory(categoryItem.name)}
                        </div>

                        <div className="vendor-category-meta">
                          <span>{translateLabel("txt_web_vendor_item_count", "{count} items", { count: categoryItem.itemsCount })}</span>

                          <span
                            className={
                              isActive
                                ? "vendor-menu-status-active"
                                : "vendor-menu-status-inactive"
                            }
                          >
                            {isActive ? translateLabel("txt_common_active", "Active") : translateLabel("txt_web_vendor_inactive", "Inactive")}
                          </span>
                        </div>
                      </div>

                      <div
                        className={`vendor-category-actions ${
                          categoryProtected
                            ? "vendor-category-actions-protected"
                            : ""
                        }`}
                      >
                        {!categoryProtected && (
                          <div className="vendor-category-action-menu">
                            {isActive && (
                              <button
                                className="vendor-category-edit-button"
                                type="button"
                                aria-expanded={
                                  openCategoryActionId ===
                                  categoryItem.menuCategoryId
                                }
                                aria-label={translateLabel("txt_web_vendor_open_actions_for_value1", "Open actions for {value1}", { value1: categoryItem.name })}
                                onClick={() =>
                                  setOpenCategoryActionId((currentId) =>
                                    currentId === categoryItem.menuCategoryId
                                      ? null
                                      : categoryItem.menuCategoryId,
                                  )
                                }
                              >
                                <span>{translateLabel("txt_web_vendor_edit", "Edit")}</span>
                              </button>
                            )}

                            {openCategoryActionId ===
                              categoryItem.menuCategoryId && (
                              <div className="vendor-category-action-dropdown">
                                <button
                                  type="button"
                                  aria-label={translateLabel("txt_web_vendor_edit_value1", "Edit {value1}", { value1: categoryItem.name })}
                                  onClick={() => {
                                    // setOpenCategoryActionId(null);
                                    openCategoryModal(categoryItem);
                                  }}
                                >
                                  <EditGrayIcon />
                                  <span>{translateLabel("txt_web_vendor_edit", "Edit")}</span>
                                </button>

                                <button
                                  type="button"
                                  aria-label={translateLabel("txt_web_vendor_delete_value1", "Delete {value1}", { value1: categoryItem.name })}
                                  disabled={
                                    !getDeletableCategoryId(categoryItem) ||
                                    deletingCategoryId ===
                                      getDeletableCategoryId(categoryItem)
                                  }
                                  onClick={() => {
                                    setOpenCategoryActionId(null);
                                    requestDeleteCategory(categoryItem);
                                  }}
                                >
                                  <TrachRedIcon />
                                  <span>{translateLabel("lnk_delete", "Delete")}</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}

              {/*/**cccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccccc */}

              {!categoriesLoading &&
                !categoryError &&
                categoryApiRows.length === 0 && (
                  <div className="vendor-menu-empty-state">
                  {translateLabel("txt_web_vendor_no_menu_categories", "No menu categories found.")}
                  </div>
                )}
            </div>
          </section>
        ) : activeTab === "addons" ? (
          <section className="vendor-addons-management">
            <div className="vendor-category-section-header">
              <div>
                <div className="vendor-category-section-title">
                  {translateLabel("txt_web_vendor_add_on_sections", "Add-on Sections")}
                </div>
                <div className="vendor-category-section-subtitle">
                  {translateLabel("txt_web_vendor_reusable_groups_that_can_later_be_linked_to_products", "Reusable groups that can later be linked to products.")}
                </div>
              </div>
              <button
                className="vendor-category-info-button"
                type="button"
                aria-label={translateLabel("txt_web_vendor_add_on_sections_information", "Add-on sections information")}
              >
                <InfoGrayIcon />
                <span role="tooltip">
                  {translateLabel("txt_web_vendor_add_on_sections_help", "Add-on sections hold options like sauces, toppings, or extras that can be attached to menu items.")}
                </span>
              </button>
            </div>

            <div className="vendor-addons-list">
              {addOnSectionsError && (
                <div className="vendor-category-list-feedback" role="alert">
                  {translateMenuFeedback(addOnSectionsError)}
                </div>
              )}

              {addOnSectionsLoading && (
                <div className="vendor-menu-empty-state">
                  {translateLabel("txt_web_vendor_loading_add_on_sections", "Loading add-on sections...")}
                </div>
              )}

              {!addOnSectionsLoading &&
                addOnSections.map((section, sectionIndex) => (
                  <article
                    className="vendor-addon-section-card"
                    key={section.id}
                  >
                    <div className="vendor-addon-section-main">
                      <div className="vendor-addon-section-header">
                        <div className="vendor-category-name">
                          {getAddOnSectionDisplayName(section, sectionIndex)}
                        </div>
                        <div className="vendor-addon-section-actions">
                          <button
                            type="button"
                            disabled={!section.addonSectionId}
                            onClick={() => openAddOnItemModal(section)}
                          >
                            <LuPlus aria-hidden="true" /> {translateLabel("txt_web_vendor_add_item", "Add Item")} </button>
                          <button
                            type="button"
                            aria-label={translateLabel("txt_web_vendor_edit_value1", "Edit {value1}", { value1: getAddOnSectionDisplayName(section, sectionIndex) })}
                            onClick={() => openAddOnSectionModal(section)}
                          >
                            <EditGrayIcon />
                          </button>
                          <button
                            type="button"
                            aria-label={translateLabel("txt_web_vendor_delete_value1", "Delete {value1}", { value1: getAddOnSectionDisplayName(section, sectionIndex) })}
                            disabled={
                              !section.addonSectionId ||
                              deletingAddOnSectionId ===
                                String(section.addonSectionId)
                            }
                            onClick={() => deleteAddOnSection(section)}
                          >
                            <TrachRedIcon />
                          </button>
                        </div>
                      </div>
                      <div className="vendor-category-meta">
                        <span>
                          {section.items.length === 1
                            ? <>1 {translateLabel("txt_end_user_item", "item")}</>
                            : translateLabel("txt_web_vendor_item_count", "{count} items", { count: section.items.length })}
                        </span>
                        <span>
                          {section.pricingType === "paid" ? translateLabel("input_addons_paid", "Paid") : translateLabel("input_addons_free", "Free")}
                        </span>
                        <span>
                          {section.allowMultiple
                            ? translateLabel("txt_web_vendor_multiple_selection", "Multiple selection")
                            : translateLabel("txt_web_vendor_single_selection", "Single selection")}
                        </span>
                        {section.maxSelections && (
                          <span>{translateLabel("txt_web_vendor_max_selections", "Max {count}", { count: section.maxSelections })}</span>
                        )}
                        <span
                          className={
                            section.active
                              ? "vendor-menu-status-active"
                              : "vendor-menu-status-inactive"
                          }
                        >
                          {section.active ? translateLabel("txt_common_active", "Active") : translateLabel("txt_web_vendor_inactive", "Inactive")}
                        </span>
                      </div>
                      <div className="vendor-addon-section-items">
                        {section.items.length > 0 ? (
                          section.items.map((item, index) => {
                            const itemId =
                              getAddOnItemBackendId(item) ||
                              `${section.id}-${index}`;

                            return (
                              <div
                                className="vendor-addon-item-row"
                                key={itemId}
                              >
                                <div>
                                  <span>{getAddOnItemDisplayName(item, index)}</span>
                                  {section.pricingType === "paid" && (
                                    <small>{formatAddOnItemPrice(item)}</small>
                                  )}
                                </div>
                                <div className="vendor-addon-item-actions">
                                  <button
                                    type="button"
                                    aria-label={translateLabel("txt_web_vendor_edit_value1", "Edit {value1}", { value1: getAddOnItemDisplayName(item, index) })}
                                    onClick={() =>
                                      openAddOnItemModal(section, item)
                                    }
                                  >
                                    <EditGrayIcon />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={translateLabel("txt_web_vendor_delete_value1", "Delete {value1}", { value1: getAddOnItemDisplayName(item, index) })}
                                    disabled={
                                      deletingAddOnItemId === String(itemId)
                                    }
                                    onClick={() =>
                                      deleteAddOnItem(section, item)
                                    }
                                  >
                                    <TrachRedIcon />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <button
                            className="vendor-addon-empty-item-button"
                            type="button"
                            disabled={!section.addonSectionId}
                            onClick={() => openAddOnItemModal(section)}
                          >
                            <LuPlus aria-hidden="true" />
                            {translateLabel("txt_web_vendor_add_first_item", "Add first item")}
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))}

              {!addOnSectionsLoading &&
                !addOnSectionsError &&
                addOnSections.length === 0 && (
                  <div className="vendor-menu-empty-state">
                    {translateLabel("txt_web_vendor_no_add_on_sections_found", "No add-on sections found.")}
                  </div>
                )}
            </div>
          </section>
        ) : (
          <div className="vendor-menu-items">
            {itemActionError && (
              <div className="vendor-menu-list-feedback" role="alert">{translateMenuFeedback(itemActionError)}</div>
            )}

            {itemsLoading && visibleItems.length === 0 && (
              <div className="vendor-menu-empty-state">
                {translateLabel("txt_web_vendor_loading_menu_items", "Loading menu items...")}
              </div>
            )}

            {visibleItems.map((item) => {
              const sourceName = getCatalogSourceName(item.rawItem);
              const displayName = sourceName || translateLabel("txt_web_vendor_unnamed_item", "Unnamed item");
              return (
              <article className="vendor-menu-item-card" key={item.id}>
                <div className="vendor-menu-item-image">
                  {item.image ? (
                    <button
                      type="button"
                      aria-label={translateLabel("txt_web_vendor_preview_value1", "Preview {value1}", { value1: displayName })}
                      onClick={() =>
                        setPreviewImage({ src: item.image, alt: sourceName || "" })
                      }
                    >
                      <img src={item.image} alt={displayName} />
                    </button>
                  ) : (
                    translateLabel("txt_end_user_image", "Image")
                  )}
                </div>

                <div className="vendor-menu-item-info">
                  <div className="vendor-menu-item-name">{displayName}</div>
                  <div className="vendor-menu-item-meta">
                    {item.category && (
                      <>
                        <span>{translateRestaurantCategory(item.category)}</span>
                        <span aria-hidden="true">•</span>
                      </>
                    )}
                    <span>{item.price}</span>
                  </div>
                </div>

                <div className="vendor-menu-item-actions">
                  <div className="vendor-menu-item-state">
                    <span
                      className={
                        item.active
                          ? "vendor-menu-status-active"
                          : "vendor-menu-status-inactive"
                      }
                    >
                      {item.active ? translateLabel("txt_common_active", "Active") : translateLabel("txt_web_vendor_inactive", "Inactive")}
                    </span>
                    {item.outOfStock && (
                      <span className="vendor-menu-out-of-stock">
                        {translateLabel("txt_web_vendor_out_of_stock", "Out of Stock")}
                      </span>
                    )}
                    <button
                      className={`vendor-menu-toggle ${
                        item.active ? "active" : ""
                      }`}
                      type="button"
                      role="switch"
                      aria-checked={item.active}
                      disabled={togglingItemId === item.id}
                      aria-label={`${item.active ? translateLabel("txt_web_vendor_deactivate", "Deactivate") : translateLabel("txt_web_vendor_activate", "Activate")} ${
                        displayName
                      }`}
                      onClick={() => toggleItem(item)}
                    >
                      <span />
                    </button>
                  </div>

                  <div className="vendor-menu-more-wrapper">
                    <button
                      className="vendor-menu-more-button"
                      type="button"
                      aria-label={translateLabel("txt_web_vendor_more_actions_for_value1", "More actions for {value1}", { value1: displayName })}
                      aria-expanded={openItemActionId === item.id}
                      onClick={() =>
                        setOpenItemActionId((currentItemId) =>
                          currentItemId === item.id ? null : item.id,
                        )
                      }
                    >
                      <LuEllipsisVertical aria-hidden="true" />
                    </button>

                    {openItemActionId === item.id && (
                      <div className="vendor-menu-action-menu">
                        <button
                          type="button"
                          aria-label={translateLabel("txt_web_vendor_edit_value1", "Edit {value1}", { value1: displayName })}
                          onClick={() => editMenuItem(item)}
                        >
                          <EditGrayIcon />
                          <span>{translateLabel("txt_web_vendor_edit", "Edit")}</span>
                        </button>
                        <button
                          type="button"
                          aria-label={translateLabel("txt_web_vendor_delete_value1", "Delete {value1}", { value1: displayName })}
                          disabled={deletingItemId === item.id}
                          onClick={() => deleteMenuItem(item)}
                        >
                          <TrachRedIcon />
                          <span>{translateLabel("lnk_delete", "Delete")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
              );
            })}

            {!itemsLoading &&
              !categoriesLoading &&
              visibleItems.length === 0 && (
                <div className="vendor-menu-empty-state">
                  {translateLabel("txt_web_vendor_no_matching_menu_items", "No menu items match your search.")}
                </div>
              )}

            {!itemsLoading && visibleItems.length > 0 && (
              <div className="vendor-menu-pagination">
                <div className="vendor-menu-pagination-summary">
                  {translateLabel("txt_web_vendor_page_number", "Page {page}", { page: itemPage })}
                  {itemPagination.totalPages > 1
                    ? ` ${translateLabel("txt_web_vendor_page_total", "of {total}", { total: itemPagination.totalPages })}`
                    : ""}
                  {itemPagination.totalElements !== null
                    ? ` - ${translateLabel("txt_web_vendor_item_count", "{count} items", { count: itemPagination.totalElements })}`
                    : ""}
                </div>

                <div className="vendor-menu-pagination-controls">
                  <label>
                    <span>{translateLabel("txt_web_vendor_rows", "Rows")}</span>
                    <select
                      value={itemPageSize}
                      onChange={(event) => {
                        setItemPageSize(Number(event.target.value));
                        setItemPage(1);
                      }}
                    >
                      {MENU_ITEMS_PAGE_SIZE_OPTIONS.map((sizeOption) => (
                        <option key={sizeOption} value={sizeOption}>
                          {sizeOption}
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    disabled={itemPage <= 1}
                    onClick={() =>
                      setItemPage((currentPage) => Math.max(1, currentPage - 1))
                    }
                  >
                    {translateLabel("txt_web_vendor_previous", "Previous")}
                  </button>
                  <button
                    type="button"
                    disabled={!itemPagination.hasNextPage}
                    onClick={() =>
                      setItemPage((currentPage) => currentPage + 1)
                    }
                  > {translateLabel("btn_next", "Next")} </button>
                </div>
              </div>
            )}
          </div>
        )}

        {CantDelete && (
          <div
            className="vendor-add-on-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setCantDelete(false);
            }}
          >
            <div
              className="vendor-category-delete-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="vendor-category-cannot-delete-title"
              aria-describedby="vendor-category-cannot-delete-description"
              onKeyDown={(event) => {
                if (event.key === "Escape") setCantDelete(false);
              }}
            >
              <button
                className="vendor-category-delete-close"
                type="button"
                aria-label={translateLabel("btn_web_vendor_close_category_deletion_message", "Close category deletion message")}
                onClick={() => setCantDelete(false)}
              >
                <LuX aria-hidden="true" />
              </button>
              <div className="vendor-category-delete-title" id="vendor-category-cannot-delete-title">
                {translateLabel("txt_web_vendor_cannot_delete_category", "Cannot delete category")}
              </div>
              <div className="vendor-category-delete-description" id="vendor-category-cannot-delete-description">
                {translateLabel("txt_web_vendor_category_has_items", "This category can't be deleted because it holds items related to it.")}
              </div>
              <div className="vendor-category-delete-actions">
                <button type="button" autoFocus onClick={() => setCantDelete(false)}>
                  {translateLabel("btn_web_vendor_category_message_ok", "OK")}
                </button>
              </div>
            </div>
          </div>
        )}

        {categoryPendingDelete && (
          <div
            className="vendor-add-on-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeDeleteCategoryDialog();
              }
            }}
          >
            <div
              className="vendor-category-delete-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-category-delete-title"
            >
              <button
                className="vendor-category-delete-close"
                type="button"
                aria-label={translateLabel("txt_web_vendor_close_delete_category_confirmation", "Close delete category confirmation")}
                disabled={Boolean(deletingCategoryId)}
                onClick={closeDeleteCategoryDialog}
              >
                <LuX aria-hidden="true" />
              </button>
              <div
                className="vendor-category-delete-title"
                id="vendor-category-delete-title"
              >
                {translateLabel("txt_web_vendor_delete_category_question", "Delete category?")}
              </div>
              <div className="vendor-category-delete-description">
                {translateLabel("txt_web_vendor_remove_category_prefix", "This will remove")}{" "}
                <strong>{translateRestaurantCategory(categoryPendingDelete.name) || translateLabel("txt_web_vendor_this_category", "this category")}</strong>{" "}
                {translateLabel("txt_web_vendor_remove_category_suffix", "from your category list.")}
              </div>
              <div className="vendor-category-delete-actions">
                <button
                  type="button"
                  disabled={Boolean(deletingCategoryId)}
                  onClick={closeDeleteCategoryDialog}
                > {translateLabel("btn_cancel", "Cancel")} </button>
                <button
                  type="button"
                  disabled={Boolean(deletingCategoryId)}
                  onClick={() => deleteCategory(categoryPendingDelete)}
                >
                  {deletingCategoryId ? translateLabel("txt_end_user_deleting", "Deleting...") : translateLabel("lnk_delete", "Delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCategoryModal && (
          <div
            className="vendor-add-on-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeCategoryModal();
            }}
          >
            <div
              className="vendor-category-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-category-modal-title"
            >
              <div className="vendor-category-modal-header">
                <div>
                  <div
                    className="vendor-category-modal-title"
                    id="vendor-category-modal-title"
                  >
                    {translateLabel("txt_web_vendor_category_menu", "Category Menu")}
                  </div>
                  <div className="vendor-category-modal-subtitle">
                    {translateLabel("txt_web_vendor_category_menu_description", "Add your category here.")}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={translateLabel("txt_web_vendor_close_category_menu", "Close Category Menu")}
                  onClick={closeCategoryModal}
                >
                  <LuX aria-hidden="true" />
                </button>
              </div>

              <form
                className="vendor-category-modal-form"
                onSubmit={saveCategory}
              >
                <label htmlFor="vendor-category-name">{translateLabel("input_category_name", "Category Name")}</label>
                <input
                  id="vendor-category-name"
                  type="text"
                  value={categoryName}
                  placeholder={translateLabel("txt_web_vendor_name_your_category", "Name your category...")}
                  onChange={(event) => setCategoryName(event.target.value)}
                  disabled={categorySaving}
                  autoFocus
                />
                {categorySubmitError && (
                  <div className="vendor-category-modal-feedback" role="alert">
                    {translateMenuFeedback(categorySubmitError)}
                  </div>
                )}
                <button type="submit" disabled={categorySaving}>
                  {categorySaving
                    ? translateLabel("txt_end_user_adding", "Adding...")
                    : editingCategoryId
                      ? translateLabel("txt_web_vendor_save_section", "Save Section")
                      : translateLabel("btn_add_section", "Add Section")}
                </button>
              </form>
            </div>
          </div>
        )}

        {showAddOnSectionModal && (
          <div
            className="vendor-add-on-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeAddOnSectionModal();
              }
            }}
          >
            <div
              className="vendor-add-on-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-menu-add-on-modal-title"
            >
              <div className="vendor-add-on-modal-header">
                <div
                  className="vendor-add-on-modal-title"
                  id="vendor-menu-add-on-modal-title"
                >
                  {editingAddOnSectionId
                    ? translateLabel("txt_web_vendor_edit_add_ons_section", "Edit Add-ons Section")
                    : translateLabel("txt_addons_section", "Add-ons Section")}
                </div>
                <button
                  type="button"
                  aria-label={translateLabel("txt_web_vendor_close_add_ons_section", "Close Add-ons Section")}
                  disabled={addOnSectionSaving}
                  onClick={() => closeAddOnSectionModal()}
                >
                  <LuX aria-hidden="true" />
                </button>
              </div>

              <form
                className="vendor-add-on-modal-form"
                onSubmit={saveAddOnSection}
              >
                <div className="vendor-add-on-modal-field">
                  <label htmlFor="vendor-menu-add-on-section-name"> {translateLabel("input_section_name", "Section Name")} </label>
                  <input
                    id="vendor-menu-add-on-section-name"
                    type="text"
                    value={addOnSectionName}
                    placeholder={translateLabel("input_section_name", "Section name")}
                    disabled={addOnSectionSaving}
                    aria-invalid={Boolean(addOnSectionSubmitError)}
                    onChange={(event) => {
                      setAddOnSectionName(event.target.value);
                      if (addOnSectionSubmitError) {
                        setAddOnSectionSubmitError("");
                      }
                    }}
                    autoFocus
                  />
                  {addOnSectionSubmitError && (
                    <div className="vendor-add-on-modal-error" role="alert">
                      {translateMenuFeedback(addOnSectionSubmitError)}
                    </div>
                  )}
                </div>
                {editingAddOnSectionId ? null : (
                  <div
                    className="vendor-add-on-pricing-tabs"
                    role="group"
                    aria-label={translateLabel("txt_web_vendor_add_on_pricing", "Add-on pricing")}
                  >
                    <button
                      className={addOnSectionType === "free" ? "active" : ""}
                      type="button"
                      disabled={addOnSectionSaving}
                      onClick={() => setAddOnSectionType("free")}
                    > {translateLabel("input_addons_free", "Free")} </button>
                    <button
                      className={addOnSectionType === "paid" ? "active" : ""}
                      type="button"
                      disabled={addOnSectionSaving}
                      onClick={() => setAddOnSectionType("paid")}
                    > {translateLabel("input_addons_paid", "Paid")} </button>
                  </div>
                )}
                <div className="vendor-add-on-multiple-row">
                  <div>
                    <label>{translateLabel("input_allow_multiple_selections", "Allow Multiple Selections")}</label>
                    <div className="vendor-add-on-multiple-description">
                      {addOnAllowMultiple
                        ? translateLabel("txt_web_vendor_customers_can_choose_more_than_one_option", "Customers can choose more than one option")
                        : translateLabel("txt_web_vendor_selection_becomes_radio_button_single_choice", "Selection becomes radio button (single choice)")}
                    </div>
                  </div>
                  <button
                    className={`vendor-add-on-multiple-toggle ${
                      addOnAllowMultiple ? "active" : ""
                    }`}
                    type="button"
                    role="switch"
                    aria-checked={addOnAllowMultiple}
                    aria-label={translateLabel("input_allow_multiple_selections", "Allow multiple selections")}
                    disabled={addOnSectionSaving}
                    onClick={() => {
                      const nextAllowMultiple = !addOnAllowMultiple;
                      setAddOnAllowMultiple(nextAllowMultiple);
                      setAddOnAllowedSelections((currentValue) =>
                        nextAllowMultiple
                          ? sanitizePositiveIntegerInput(currentValue) || "1"
                          : "1",
                      );
                    }}
                  >
                    <span />
                  </button>
                </div>

                <div className="vendor-add-on-modal-field">
                  <label htmlFor="vendor-menu-add-on-selection-limit"> {translateLabel("input_allowed_selections_count", "Number of Allowed Selections")} </label>
                  <input
                    id="vendor-menu-add-on-selection-limit"
                    type="text"
                    inputMode="numeric"
                    value={addOnAllowMultiple ? addOnAllowedSelections : "1"}
                    disabled={!addOnAllowMultiple || addOnSectionSaving}
                    onChange={(event) => {
                      if (!addOnAllowMultiple) return;

                      setAddOnAllowedSelections(
                        sanitizePositiveIntegerInput(event.target.value),
                      );
                    }}
                  />
                  <small>
                    {addOnAllowMultiple
                      ? translateLabel("txt_web_vendor_example_3_max_3_items", "Example: 3 = max 3 items")
                      : translateLabel("txt_web_vendor_single_selection_allows_1_item_only", "Single selection allows 1 item only")}
                  </small>
                </div>

                <div className="vendor-add-on-modal-actions">
                  <button
                    className="vendor-add-on-modal-cancel"
                    type="button"
                    disabled={addOnSectionSaving}
                    onClick={() => closeAddOnSectionModal()}
                  > {translateLabel("btn_cancel", "Cancel")} </button>
                  <button
                    className="vendor-add-on-modal-create"
                    type="submit"
                    disabled={addOnSectionSaving}
                  >
                    {addOnSectionSaving
                      ? translateLabel("txt_end_user_saving", "Saving...")
                      : editingAddOnSectionId
                        ? translateLabel("txt_web_vendor_update_section", "Update Section")
                        : translateLabel("btn_create_section", "Create Section")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showAddOnItemModal && (
          <div
            className="vendor-add-on-modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeAddOnItemModal();
              }
            }}
          >
            <div
              className="vendor-add-on-item-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-menu-add-on-item-modal-title"
            >
              <div className="vendor-add-on-modal-header">
                <div
                  className="vendor-add-on-modal-title"
                  id="vendor-menu-add-on-item-modal-title"
                >
                  {editingAddOnItemId
                    ? translateLabel("txt_web_vendor_edit_add_on_item", "Edit Add-on Item")
                    : translateLabel("txt_web_vendor_add_add_on_item", "Add Add-on Item")}
                </div>
                <button
                  type="button"
                  aria-label={translateLabel("txt_web_vendor_close_add_on_item", "Close Add-on Item")}
                  disabled={addOnItemSaving}
                  onClick={() => closeAddOnItemModal()}
                >
                  <LuX aria-hidden="true" />
                </button>
              </div>

              <form
                className="vendor-add-on-item-modal-form"
                onSubmit={saveAddOnItem}
              >
                <div className="vendor-add-on-modal-field">
                  <label htmlFor="vendor-menu-add-on-item-name"> {translateLabel("txt_web_vendor_item_name", "Item Name")} </label>
                  <input
                    id="vendor-menu-add-on-item-name"
                    type="text"
                    value={addOnItemName}
                    placeholder={translateLabel("txt_web_vendor_item_name", "Item name")}
                    disabled={addOnItemSaving}
                    aria-invalid={Boolean(addOnItemSubmitError)}
                    onChange={(event) => {
                      setAddOnItemName(event.target.value);
                      if (addOnItemSubmitError) {
                        setAddOnItemSubmitError("");
                      }
                    }}
                    autoFocus
                  />
                </div>

                {addOnSections.find(
                  (section) =>
                    String(section.id) === String(activeAddOnItemSectionId),
                )?.pricingType === "paid" && (
                  <div className="vendor-add-on-item-price-row">
                    <div className="vendor-add-on-modal-field">
                      <label htmlFor="vendor-menu-add-on-item-price"> {translateLabel("input_price", "Price")} </label>
                      <input
                        id="vendor-menu-add-on-item-price"
                        type="text"
                        inputMode="decimal"
                        value={addOnItemPrice}
                        placeholder="0.00"
                        disabled={addOnItemSaving}
                        onChange={(event) =>
                          setAddOnItemPrice(
                            sanitizeDecimalInput(event.target.value),
                          )
                        }
                      />
                    </div>

                    <div className="vendor-add-on-modal-field">
                      <label htmlFor="vendor-menu-add-on-item-currency"> {translateLabel("lnk_currency", "Currency")} </label>
                      <select
                        id="vendor-menu-add-on-item-currency"
                        value={addOnItemCurrency}
                        disabled={addOnItemSaving}
                        onChange={(event) =>
                          setAddOnItemCurrency(event.target.value)
                        }
                      >
                        <option value="USD">{translateLabel("txt_currency_usd", "USD")}</option>
                        <option value="LBP">LBP</option>
                      </select>
                    </div>
                  </div>
                )}

                {addOnItemSubmitError && (
                  <div className="vendor-add-on-modal-error" role="alert">
                    {translateMenuFeedback(addOnItemSubmitError)}
                  </div>
                )}

                <div className="vendor-add-on-modal-actions">
                  <button
                    className="vendor-add-on-modal-cancel"
                    type="button"
                    disabled={addOnItemSaving}
                    onClick={() => closeAddOnItemModal()}
                  > {translateLabel("btn_cancel", "Cancel")} </button>
                  <button
                    className="vendor-add-on-modal-create"
                    type="submit"
                    disabled={addOnItemSaving}
                  >
                    {addOnItemSaving
                      ? translateLabel("txt_end_user_saving", "Saving...")
                      : editingAddOnItemId
                        ? translateLabel("txt_web_vendor_update_item", "Update Item")
                        : translateLabel("txt_web_vendor_create_item", "Create Item")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {previewImage && (
          <div
            className="vendor-image-preview-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closePreviewImage();
            }}
          >
            <div
              className="vendor-image-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-label={previewImage.alt || translateLabel("txt_web_vendor_unnamed_item", "Unnamed item")}
            >
              <button
                type="button"
                className="vendor-image-preview-close"
                aria-label={translateLabel("txt_web_vendor_close_image_preview", "Close image preview")}
                onClick={closePreviewImage}
              >
                <LuX aria-hidden="true" />
              </button>
              <img src={previewImage.src} alt={previewImage.alt || translateLabel("txt_web_vendor_unnamed_item", "Unnamed item")} />
            </div>
          </div>
        )}
        {Imp && (
          <ProductImportModal
            onClose={() => setImp(false)}
            onImported={() => setImportRefresh((value) => value + 1)}
          />
        )}
        {Exp && (
          <ProductExportModal
            onClose={() => setExp(false)}
            onExport={exportVendorProducts}
          />
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorMenu;
